/* GUEST Main Entry Point */
/* (C) 2024 - 2025, by Drew Anderson  - Cloud Media Works, LLC - All Rights Reserved */

import React, { useEffect, useRef, useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
// [Guest loop fix 2026-08-14] Pure decision logic, shared with the node test suite.
import {
  authTimestampGateApplies,
  isAuthTokenStale,
  bumpGuestSignInAttempts,
  resetGuestSignInAttempts,
  purgeStaleAuthKeys,
} from '../utils/guestGate';
import { Header } from '../components/header/Header';
import { Footer } from '../components/footer/Footer';

import { RealtimeClient, SessionResourceType } from '../lib/RealtimeClient';

import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';
import { injectAssistantJsonLd } from '../utils/seoUtils';
import ChatUI from '../components/ChatUI';
import ChatUIWhite from '../components/ChatUIWhite';
//import ChatUIBot from '../components/ChatUIBot';
import ChatUIMin from '../components/ChatUIMin';
import { isGuestTokenExpired, isGuestTokenNearExpiry, refreshGuestToken, reResolveGuestOrg } from '../utils/guestSignIn';
import { logRelay } from '../utils/client-logger';

import { showSessionExpiredUI, isSessionExpiredLocked } from '../modules/sessionExpiredUI';

import { fetchPromptSession } from '../utils/openaiAPI';
import { createBotMetricClient, sendMetricsImmediate, sendMetricsAsync, clearMetricQueue, getAssistant, getAssistantConfig, getAssistantSettings, getBotSettings, getChatHistoryByRunId } from '../utils/botFactoryAPI';
import { buildSelectedBotFromRecord, buildBotSettingsPayload, botUnavailableState, storeBotSettings } from '../utils/botIdentity';
import { fetchAgentBootstrap, bootstrapBotId, PREAUTH_TEXT } from '../utils/agentBootstrap';
import { startupMemoryRecall, appendMemoryRecallSession, generateCurrentSessionEntry, buildMemoryMetadataPayload } from '../lib/memoryRecall';

import './IndexPage.scss';
import { getLanguageMode, setLanguageMode, clearLanguageMode } from '../lib/languageMode';
import { useMicMode, getTurnDetection, useInteractionNoiseGate } from '../lib/micMode';
import {
  buildSurfaceCacheKey,
  hashSurface,
  getRenderedSurfaceWithFallback,
  saveRenderedSurfaceWithFallback,
  validateRenderedSurface,
  renderSurfaceLanguage,
} from '../utils/languageSurfaceCache';

const DEBUG_CONVERSATION = false; // Unhide logRelay

// Decode base64 audio to Int16Array (GA API sends audio as base64)
const base64ToInt16Array = (base64: string): Int16Array => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
};

const PAGE_TIMEOUT = 900;// 900 Timeout 15 minutes in seconds - 120 for 2 minutes
// Two timers each add 1 per 30s tick, so idle timeout = MAX_CONNECTION_ATTEMPTS / 2 × 30s: 120 = 30 min.
const MAX_CONNECTION_ATTEMPTS = 120;
// [FIX heartbeat-race] 30s, NOT 55s. The old 55000 tied Heroku's 55s WebSocket idle cap —
// any timer jitter or background-tab throttling let Heroku win the race and reap the socket
// (H15 / code 1006). 30s gives two keepalives per idle window. NOTE: the relay's startPing()
// (now wired in) is the primary server-side keepalive; this client heartbeat is defense-in-depth.
const HEARTBEAT_PING = 30000

let speechStatus: string | null = null;// Speech Status
let selectedBot: any  = null;
// [S2-B §3.2] the record's host id (legacyAssistantId || id) — set by runInit, read at both socket sites.
let selectedHostId: string | null = null;
const persistSelectedBot = (bot: any, fallbackName?: string) => {
  if (!bot) return;
  const setOrRemove = (key: string, value?: string | null) => {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  };

  try {
    const name = bot?.name || bot?.title || fallbackName || 'AI Assistant';

    // sanitize optional fields so stale values don't linger
    const cleaned = { ...bot };
    if (!bot?.avatar) cleaned.avatar = '';
    if (!bot?.thumbnail) cleaned.thumbnail = '';

    localStorage.setItem('SelectedBot', JSON.stringify(cleaned));
    localStorage.setItem('selectedBotName', name || '');
    setOrRemove('selectedBotAvatar', bot?.avatar || bot?.thumbnail);
    setOrRemove('selectedBotThumbnail', bot?.thumbnail || bot?.avatar);
    setOrRemove('selectedBotThemeBg', bot?.theme?.backgroundColor);
    setOrRemove('selectedBotThemeFg', bot?.theme?.foregroundColor);
    setOrRemove('selectedBotFooterBg', bot?.footer?.backcolor);
  } catch {
    // ignore persist errors
  }
};
const clearSelectedBotSnapshot = () => {
  try {
    ['SelectedBot', 'selectedBotName', 'selectedBotAvatar', 'selectedBotThumbnail', 'selectedBotThemeBg', 'selectedBotThemeFg', 'selectedBotFooterBg'].forEach((k) =>
      localStorage.removeItem(k)
    );
    (window as any).selectedBot = null;
  } catch {
    // ignore clearing errors
  }
};


// Re-read at metadata-build time (2026-10-28). `user` below is captured ONCE at module load;
// a sign-in whose localStorage write lost the race to the /ibot navigation left it as the
// placeholder (org is a string) for the life of the page, so org.orgId went out empty.
const readStoredUser = (): any | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw && raw !== "null" ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storedUser = localStorage.getItem("user");
const user = storedUser && storedUser !== "null" ? JSON.parse(storedUser) : { // see src/utils/guestSignIn.ts
  id: localStorage.getItem('userId'),
  org: "Cloud Media Works, LLC",
  username: localStorage.getItem('name'),
  email: "",
  firstname: localStorage.getItem('name'),
  lastname: "User",
  emailVerified: false,
  verificationToken: "",
  role: "guest",
  mobile: "",
  pic: "/assets/app/avatar-icon.jpg",
  system: false,
};

interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}
interface ExtendedSessionResourceType extends SessionResourceType {
  assistant_id?: string;
  voice?: any;
}



// 🔁 Init [Ping] Tracker
window.__ping = {
  connectionAttempts: 1,
  pingTimestamp: Date.now()
};
// Dynamically add voice toggle without altering Window type
const storedToggle = localStorage.getItem('voiceToggle');
(window.__ping as any).voiceToggle = storedToggle === 'true' ? true : false;
localStorage.setItem('voiceToggle', String((window.__ping as any).voiceToggle));

// 🔁 [Ping] Declare Reset - Only Reset if Below Threshold
window.resetPingTimer = () => {
  // Always reset attempts on real activity so heartbeats don't trip a disconnect during long operations
  window.__ping.connectionAttempts = 1;
  logRelay('[IndexPage][Ping] 🔄 Ping counter reset to 1');

  window.__ping.pingTimestamp = Date.now();
  document.cookie = `pingTimestamp=${window.__ping.pingTimestamp}; path=/`;

  const readable = new Date(window.__ping.pingTimestamp).toLocaleString();
  logRelay(`[IndexPage][Ping] ⏱️ Ping timestamp set: ${window.__ping.pingTimestamp} (${readable})`);

  (window.__ping as any).voiceToggle = !(window.__ping as any).voiceToggle;
  localStorage.setItem('voiceToggle', String((window.__ping as any).voiceToggle));

  const isSilent = (window.__ping as any).voiceToggle === true;

  if (isSilent) {
    logRelay('[IndexPage][Ping] 🔊 Ping Silent');
  } else {
    logRelay('[IndexPage][Ping] 🔊 Ping Active');
  }
};

/**
 * Realtime relay server URL
 */
const relayEnvBase = process.env.REACT_APP_RELAY_SERVER_URL || '';
const hasWindow = typeof window !== 'undefined';
const isLocalHost = hasWindow && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
const isLocalRelayEnv = /localhost|127\.0\.0\.1/i.test(relayEnvBase);
const relayHttpBase =
  relayEnvBase && !(isLocalRelayEnv && !isLocalHost)
    ? relayEnvBase
    : (hasWindow ? window.location.origin : '');
const RELAY_SERVER_URL: string = relayHttpBase.replace(/^http/, 'ws');

if (!RELAY_SERVER_URL) {
  console.error('RELAY_SERVER_URL is not defined. Check environment variables.');
}



export function IndexPageGuest() {
  //const audioItemIdRef = useRef(null);

  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [isRecording, setIsRecording] = useState(false);

  // Mic mode: 'hf' = hands-free (default), 'ptt' = Push-to-Talk (hold, or tap once to keep the mic
  // open and tap again to send). Shared with the Settings panel through src/lib/micMode.ts.
  const { micMode, micModeRef } = useMicMode();
  const isInteractionNoise = useInteractionNoiseGate();
  useEffect(() => {
    logRelay(`[MicMode] Mic mode set: ${micMode}`);
  }, [micMode]);
  const [micBlocked, setMicBlocked] = useState(false); // permission denied or no input device
  const [assistantSpeaking, setAssistantSpeaking] = useState(false); // mirrors isSpeakingRef for the bar
  const inputLevelRef = useRef(0); // latest mic input level (0 to 1), read by the bar
  const startRecordingRef = useRef<((opts?: { interrupt?: boolean }) => Promise<void>) | null>(null);

  // [MemoryRecall] Stable ref to latest items — used in beforeunload to generate session summary.
  const itemsRef = useRef<any[]>([]);
  // [MemoryRecall] THIS page's recall bot — set ONLY when this bot has the MemoryRecall
  // flag. Replaces the old global localStorage 'ibot.memoryRecall.activeBot' slot, which
  // was shared across bots AND tabs: a visit to any other bot left/overwrote it, and the
  // unload handler then saved this page's conversation under the WRONG bot's memory key
  // (cross-bot memory bleed — e.g. DeepTag sessions recalled by Ask Steve).
  const memoryRecallBotIdRef = useRef<string | null>(null);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const [showEventsLog, setShowEventsLog] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(900); // 900 - 15 minutes Countdown timer in seconds - 120 = 2 minutes
  const [threadId, setThread] = useState<string | null>(null);
  const [runId, setRun] = useState<string | null>(null);
  const [webSessionId, setWebSessionId] = useState<string>('');
  const [clicksPerPrompt, setClicksPerPrompt] = useState<number>(0);
  const [promptClicks, setPromptClicks] = useState<Record<string, { count: number; lastClicked: string }>>({});
  const [uniqueQueries, setUniqueQueries] = useState<number>(0);
  const [questionsPerSession, setQuestionsPerSession] = useState<number>(0);
  const [engagementTimeSeconds, setEngagementTimeSeconds] = useState<number>(0);
  const [hasEngagementStarted, setHasEngagementStarted] = useState(false);
  const [elementClicks, setElementClicks] = useState<Record<string, { count: number; lastClicked: string }>>({});
  const [searchParams] = useSearchParams();
  const [guestLoopError, setGuestLoopError] = useState<string | null>(null);
  // [S2-A hotfix] Bumped by runInit once the file-path RealtimeClient exists; arms the connect effect below.
  const [botReady, setBotReady] = useState(0);
  const getSignInUrl = useCallback(() => {
    const signInUrl = new URL(window.location.origin);
    signInUrl.pathname = '/signin';
    const currentId = searchParams.get('id');
    const currentPrompt = searchParams.get('prompt');
    if (currentId) signInUrl.searchParams.set('id', currentId);
    if (currentPrompt) signInUrl.searchParams.set('prompt', currentPrompt);
    return signInUrl.toString();
  }, [searchParams]);

  // [Guest token freshness] A returning visitor reuses the guest JWT stored in
  // localStorage, but a fresh one is only minted on /guest — landing directly on
  // /ibot-guest (bookmark, share link, embed) skips the mint. If that stored token
  // has expired (default 6h lifetime), authed calls that enforce `exp` — notably bot
  // metrics — silently 401, so we lose analytics for any returning guest with no
  // visible symptom. Detect an expired guest token at init and re-mint via the /guest
  // flow BEFORE the session starts (full redirect = clean, consistent identity).
  // [S2-A §2.6.1] Extended to the ABSENT-token case: a handed-out /ibot-guest link (bookmark,
  // embed) arrives with nothing stored, and this page never mints. Before this the 6 s poll
  // caught that case with an alert and a bounce to /signin; now it is ONE purged, bounded redirect
  // to /guest at init, and the poll only covers a mint genuinely in flight. Near-expiry tokens are
  // refreshed in place (Note 5) so a long tab keeps its userId.
  useEffect(() => {
    const stored = localStorage.getItem('token');
    const cookieToken = document.cookie.split('; ').find((r) => r.startsWith('token='))?.split('=')[1];
    const guestMode = localStorage.getItem('guestMode') === 'true';
    const absent = !stored && !cookieToken;
    const expired = guestMode && !!stored && isGuestTokenExpired(stored);
    if (!absent && !expired) {
      if (guestMode && stored && isGuestTokenNearExpiry(stored)) {
        void refreshGuestToken().then((t) => {
          if (t) console.info('[GuestToken] refreshed in place before session start');
        });
      }
      return;
    }
    console.warn(`[GuestToken] ${absent ? 'no stored token' : 'stored guest token expired'} — minting via /guest before session start`);
    // ⛔ FIX 4 family — bounded, and purged, or a leftover guestMode='true' with no token loops.
    const { attempts, exceeded } = bumpGuestSignInAttempts(sessionStorage);
    if (exceeded) {
      logRelay(`⛔ Guest mint redirect bounced ${attempts}x — stopping instead of looping`);
      setGuestLoopError(
        `Guest sign-in did not complete after ${attempts} attempts. The stored session looks stale — `
        + 'clearing site data for this page, or signing in again, should resolve it.',
      );
      return;
    }
    purgeStaleAuthKeys({ localStorage, sessionStorage, document });
    const url = new URL(window.location.origin);
    url.pathname = '/guest';
    const id = searchParams.get('id');
    const prompt = searchParams.get('prompt');
    if (id) url.searchParams.set('id', id);
    if (prompt) url.searchParams.set('prompt', prompt);
    window.location.replace(url.toString());
  }, [searchParams]);
  const [contextSource, setContextSource] = useState<string>('openai');
  const [linkedSessionId, setLinkedSessionId] = useState<string | null>(() => searchParams.get('ses'));
  const [seedHistory, setSeedHistory] = useState<any[] | null>(null);
  let [assistantId, setAssistantId] = useState<string | null>(null);
  const allowedVoices = ["marin", "cedar", "coral", "ash", "alloy", "ballad", "echo", "sage", "shimmer", "verse"] as const;
  let selectedVoice = "alloy";
  const STOP_BUTTON_SENDS_STOP = true; // When true, stop-voice also sends a STOP command (Cmd+M equivalent)
  const mediaStreamRef = useRef<MediaStream | null>(null);//Microphone keep alive
  const sessionExpiredRef = useRef(isSessionExpiredLocked());
 
  const isRecordingRef = useRef(isRecording); // 🔄 Restores ping guard for user speech
  const recordingStartTimeRef = useRef<number>(0); // [PTT] Track recording start time
  const threadReceivedRef = useRef(false); // [FIX mic-bug-07] Guard against duplicate threadCreated events
  const uniqueQueriesSetRef = useRef<Set<string>>(new Set());
  const sessionStartRef = useRef<number | null>(null);
  const metricsPostedRef = useRef(false);
  // Signature of the interactions in the LAST metrics snapshot we sent. Replaces the old
  // permanent once-per-page-load latch: we now re-send an updated snapshot whenever the user
  // has interacted since the last send (e.g. after returning from a backgrounded tab), but
  // skip redundant identical sends. Sends are keyed by sessionId; botapi upserts on sessionId
  // so repeated sends UPDATE one row rather than duplicating it. See finalizeAndPostMetrics.
  const lastSentMetricsSigRef = useRef<string>('');
  const contextHydratedRef = useRef(false);
 
  //Visualizations
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);

  const isSpeakingRef = useRef(false);
  const eventBoundRef = useRef(true);
  const functionCallPendingRef = useRef(false);
  const suppressedAssistantIdsRef = useRef<Set<string>>(new Set());

  const saySuppressRef = useRef(false); // When true, suppress next SAY assistant response from display
  const promptSentRef = useRef(false); // Prevent sending querystring prompt multiple times

  // Reset Timeout
  const resetTimeout = PAGE_TIMEOUT;// 15 minutes to sync with relay server
  

  // State variable to control speech
  const stopSpeechRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(isMuted);
  const [translatedSurface, setTranslatedSurface] = useState<unknown>(null);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    if (!sessionExpiredRef.current && !isSessionExpiredLocked()) return;
    sessionExpiredRef.current = true;
    setIsConnected(false);
    showSessionExpiredUI();
  }, []);

  const statusThinkingVisibleRef = useRef(false);
  const setStatusThinking = useCallback((isVisible: boolean) => {
    statusThinkingVisibleRef.current = isVisible;
    const indicators = Array.from(document.querySelectorAll<HTMLImageElement>('.chat-status-thinking'));
    indicators.forEach((img, idx) => {
      const isLast = idx === indicators.length - 1;
      img.style.display = isVisible && isLast ? 'block' : 'none';
    });
  }, []);

  const heartbeatWorkerRef = useRef<Worker | null>(null);

const makeAssistantItem = (text: string, overrides: Record<string, any> = {}) => ({
    id: `assistant-${Date.now()}`,
    object: 'realtime.item',
    type: 'message' as const,
    status: 'completed' as const,
    role: 'assistant' as const,
    content: [{ type: 'text' as const, text }],
    formatted: { text },
  ...overrides,
} as any);

const isGuestPlaceholderName = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return /^guest[\s_-]*\d+$/i.test(value.trim());
};

  useEffect(() => {
    logRelay('[Init] window.location.href:', window.location.href);
    logRelay('[Init] Raw querystring:', window.location.search);

    const deepLinked = linkedSessionId || searchParams.get('ses');
    const qsPrompt = searchParams.get('prompt');
    const storedSession = sessionStorage.getItem('webSessionId');
    logRelay('[Init] deepLinked ses:', deepLinked, '| storedSession:', storedSession);

    // Snapshot current page as a potential return target for guest sign-in bounce logic
    try {
      const href = window.location.href;
      sessionStorage.setItem('guestOrigin', href);
      localStorage.setItem('guestOrigin', href);
      logRelay('[GuestOrigin] Stored current page as guestOrigin:', href);
    } catch (e) {
      logRelay('[GuestOrigin] Failed to store guestOrigin:', (e as any)?.message);
    }

    // Get Prompt from Query String
    logRelay('[Init] Querystring prompt:', qsPrompt);
    if (qsPrompt && qsPrompt.trim() !== '') {
      localStorage.setItem('pendingPrompt', qsPrompt);
    }

    if (deepLinked) {
      try {
        sessionStorage.setItem('webSessionId', deepLinked);
        localStorage.setItem('webSessionId', deepLinked);
        logRelay('[Session] Loaded sessionId from querystring:', deepLinked);
      } catch (err) {
        logRelay('[Session] Failed to persist querystring sessionId:', (err as any)?.message);
      }
      setWebSessionId(deepLinked);
      const storedStart = sessionStorage.getItem('sessionStartMs');
      if (storedStart) {
        sessionStartRef.current = parseInt(storedStart, 10);
      }
      // Attempt to load prior chat history for this session id
      (async () => {
        try {
          const token =
            document.cookie
              .split('; ')
              .find((row) => row.startsWith('token='))
              ?.split('=')[1] || localStorage.getItem('token') || '';
          if (!token) {
            logRelay('[ChatHistory] No auth token; skipping history fetch.');
            return;
          }
          const history = await getChatHistoryByRunId(
            deepLinked,
            token,
            localStorage.getItem('selectedBot') || undefined
          );
          if (history && history.length) {
            logRelay('[ChatHistory] Loaded history items:', history.length);
            setSeedHistory(history);
          } else {
            logRelay('[ChatHistory] No history found for session:', deepLinked);
          }
        } catch (err) {
          logRelay('[ChatHistory] Failed to load history:', (err as any)?.message || err);
        }
      })();
    } else if (storedSession) {
      setWebSessionId(storedSession);
      try {
        localStorage.setItem('webSessionId', storedSession);
        logRelay('[Session] Loaded sessionId from sessionStorage:', storedSession);
      } catch (err) {
        logRelay('[Session] Failed to mirror stored sessionId:', (err as any)?.message);
      }
      const storedStart = sessionStorage.getItem('sessionStartMs');
      if (storedStart) {
        sessionStartRef.current = parseInt(storedStart, 10);
      }
    } else {
      const generated =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem('webSessionId', generated);
      localStorage.setItem('webSessionId', generated);
      logRelay('[Session] Generated new sessionId:', generated);
      setWebSessionId(generated);
    }

    const finalSes =
      deepLinked ||
      storedSession ||
      sessionStorage.getItem('webSessionId') ||
      localStorage.getItem('webSessionId');
    logRelay('[Session] Active webSessionId after init:', finalSes);
  }, []);

  const startEngagement = useCallback(() => {
    if (!sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      try {
        sessionStorage.setItem('sessionStartMs', String(sessionStartRef.current));
      } catch (err) {
        // ignore storage issues
      }
    }
    setHasEngagementStarted(true);
  }, []);

  const handlePromptClicked = useCallback(
    (promptKey: string) => {
      startEngagement();
      setClicksPerPrompt((prev) => prev + 1);
      setPromptClicks((prev) => {
        const now = new Date().toISOString();
        const prevEntry = prev[promptKey];
        const prevCount = typeof prevEntry === 'number' ? prevEntry : prevEntry?.count || 0;
        return {
          ...prev,
          [promptKey]: { count: prevCount + 1, lastClicked: now },
        };
      });
    },
    [startEngagement]
  );

  const handleUserQuestion = useCallback(
    (cleanMessage: string, options?: { fromPrompt?: boolean }) => {
      startEngagement();
      setQuestionsPerSession((prev) => prev + 1);
      if (options?.fromPrompt) {
        return; // prompts should not count toward uniqueQueries
      }
      const normalized = cleanMessage.trim().toLowerCase();
      if (normalized) {
        uniqueQueriesSetRef.current.add(normalized);
        setUniqueQueries(uniqueQueriesSetRef.current.size);
      }
    },
    [startEngagement]
  );

  useEffect(() => {
    if (!hasEngagementStarted) return;

    const interval = setInterval(() => {
      // Keep counting for the whole live session — a snapshot may have already been sent
      // (e.g. on tab-hide) but the session can continue, and a later send must carry the
      // true total engagement, not a value frozen at the first send.
      if (sessionStartRef.current) {
        setEngagementTimeSeconds(Math.floor((Date.now() - sessionStartRef.current) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [hasEngagementStarted]);

  useEffect(() => {
    contextHydratedRef.current = false;
  }, [linkedSessionId, webSessionId, contextSource]);

  // Seed UI with prior chat history (if loaded via ses querystring)
  useEffect(() => {
    if (seedHistory && seedHistory.length && items.length === 0) {
      const seeded = seedHistory.map((h, idx) => ({
        id: `seed-${idx}`,
        role: h.role === 'assistant' ? 'assistant' : 'user',
        status: 'completed',
        formatted: { text: h.transcript || '' },
        content: [{ type: 'text', text: h.transcript || '' }],
      }));
      logRelay('[ChatHistory] Seeding UI with prior messages:', seeded.length);
      setItems(seeded as any);
    }
  }, [seedHistory, items.length, setItems]);

  const formatEngagement = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const finalizeAndPostMetrics = useCallback(
    (reason: string) => {
      // A deliberate hold (e.g. an in-progress reset) still suppresses sends. The
      // once-per-page-load latch is gone — dedup is now by interaction signature below,
      // so a later trigger carrying NEW activity (the timeout after a tab-resume) can send.
      if (metricsHoldRef.current || sessionStorage.getItem('metricsHold') === '1') {
        console.info(`[Metrics] ⏭️ finalize SKIPPED (${reason}) — blocked by metrics hold`);
        return;
      }
      const engagementSeconds =
        sessionStartRef.current !== null
          ? Math.floor((Date.now() - sessionStartRef.current) / 1000)
          : engagementTimeSeconds;

      const sessionId = sessionStorage.getItem('webSessionId') || webSessionId;
      const token =
        document.cookie
          .split('; ')
          .find((row) => row.startsWith('token='))
          ?.split('=')[1] || localStorage.getItem('token') || '';
      const memberId = user.id || localStorage.getItem('userId') || '';
      const orgId =
        localStorage.getItem('orgId') ||
        (typeof (user as any)?.org === 'string'
          ? (user as any).org
          : ((user as any)?.org?.id as string)) ||
        '';

      if (!sessionId) {
        // console.warn(`[Metrics] Missing webSessionId, skipping finalize (${reason}).`);
        return;
      }

      const storedStart = sessionStorage.getItem('sessionStartMs');
      const createdAtMs = Date.now();
      const safeEngagementSeconds = Math.max(engagementSeconds, 1);
      const parsedStored = storedStart ? parseInt(storedStart, 10) : NaN;
      const startCandidate = Number.isFinite(sessionStartRef.current as number)
        ? (sessionStartRef.current as number)
        : parsedStored;
      const sessionStartMs = Number.isFinite(startCandidate)
        ? startCandidate
        : createdAtMs - safeEngagementSeconds * 1000;
      const sessionStartIso = new Date(sessionStartMs).toISOString();
      const createdAtIso = new Date(createdAtMs).toISOString();
      const durationSeconds = Math.max(1, Math.round((createdAtMs - sessionStartMs) / 1000));

      const resolvedBotName = (selectedBot as any)?.name || resolveBotNameById(assistantId) || null;
      try {
        localStorage.setItem('selectedBotName', resolvedBotName || '');
      } catch (err) {
        // ignore storage issues
      }

      const storedName = localStorage.getItem('name');
      const resolvedUserName =
        (storedName && storedName !== 'null' ? storedName : null) ||
        user?.username ||
        user?.firstname ||
        user?.lastname ||
        'none';

  const payload = {
    createdOn: sessionStartIso, // session start time
    botId: assistantId || '',
    botName: resolvedBotName,
        orgId: orgId || '',
        memberId: memberId || undefined,
        userName: resolvedUserName,
        sessionId,
        clicksPerPrompt,
        promptClicks: Object.entries(promptClicks).map(([prompt, meta]) => {
          const count = typeof meta === 'number' ? meta : meta.count;
          const lastClicked =
            typeof meta === 'number'
              ? new Date().toISOString()
              : meta.lastClicked || new Date().toISOString();
          return { prompt, count, lastClicked };
        }),
        elementClicks: Object.entries(elementClicks).map(([element, meta]) => ({
          element,
          count: meta.count,
          lastClicked: meta.lastClicked,
        })),
        uniqueQueries,
        questionsPerSession,
        engagementTime: formatEngagement(durationSeconds),
        endedOn: createdAtIso, // time session end and of metric creation
      };

      // Ensure botName stays attached on send
      const payloadWithName = {
        ...payload,
        botName: resolvedBotName,
      };

      // logRelay('[Metrics] finalizeAndPostMetrics payload', { reason, sessionId, botId: assistantId });
      // logRelay('[Metrics] token present?', !!token, 'length', token ? token.length : 0);
      try {
        localStorage.setItem('last_metrics_payload', JSON.stringify(payloadWithName, null, 2));
      } catch (err) {
        // console.warn('[Metrics] failed to persist last_metrics_payload', err);
      }

      // Interaction signature (engagement TIME is deliberately excluded) so we re-send an
      // updated snapshot whenever the user has interacted since the last send — e.g. the
      // timeout after returning from a hidden tab — but skip redundant identical sends (the
      // +20ms/+150ms backup re-fires, or repeated tab-hides with no new activity).
      const totalPromptClicks = payload.promptClicks.reduce((n, x) => n + (x.count || 0), 0);
      const totalElementClicks = payload.elementClicks.reduce((n, x) => n + (x.count || 0), 0);
      const hasActivity =
        clicksPerPrompt > 0 || questionsPerSession > 0 || uniqueQueries > 0 ||
        totalPromptClicks > 0 || totalElementClicks > 0;
      if (!hasActivity) {
        console.info(`[Metrics] ⏭️ finalize SKIPPED (${reason}) — no activity to record yet`);
        return;
      }
      const interactionSig = JSON.stringify({
        s: sessionId, c: clicksPerPrompt, q: questionsPerSession,
        u: uniqueQueries, p: totalPromptClicks, e: totalElementClicks,
      });
      if (interactionSig === lastSentMetricsSigRef.current) {
        console.info(`[Metrics] ⏭️ finalize SKIPPED (${reason}) — no new activity since last send | session=${sessionId}`);
        return;
      }
      // Latch the signature SYNCHRONOUSLY (before the async send) so overlapping lifecycle
      // events and the backup timers can't double-send the same snapshot.
      lastSentMetricsSigRef.current = interactionSig;
      metricsPostedRef.current = true;
      try {
        sessionStorage.setItem('metricsSent', '1');
      } catch (err) {
        // ignore storage issues
      }
      console.info(
        `[Metrics] ⬆️ finalize SEND (${reason}) | session=${sessionId} | ` +
        `clicksPerPrompt=${clicksPerPrompt} questions=${questionsPerSession} uniqueQueries=${uniqueQueries} ` +
        `promptClicks=${totalPromptClicks} elementClicks=${totalElementClicks} engagement=${payload.engagementTime}`
      );
      (async () => {
        let activeToken = token || '';
        let ok = await sendMetricsAsync(payloadWithName, activeToken);
        // Reactive guest-token refresh: if the write was rejected and the token has
        // expired (e.g. the session stayed open past the token's lifetime, which the
        // init-time guard can't catch), re-mint once and retry so the metric isn't
        // silently lost on a 401.
        if (!ok && localStorage.getItem('guestMode') === 'true' && isGuestTokenExpired(activeToken)) {
          const fresh = await refreshGuestToken(); // [S2-A Note 5] same user, not a new mint
          if (fresh) {
            activeToken = fresh;
            ok = await sendMetricsAsync(payloadWithName, activeToken);
          }
        }
        if (ok) {
          console.info(`[Metrics] ✅ write OK via keepalive fetch (${reason}) | session=${sessionId}`);
          return;
        }
        const sent = sendMetricsImmediate(payloadWithName, activeToken);
        if (sent) {
          console.info(`[Metrics] ✅ write OK via sync/beacon fallback (${reason}) | session=${sessionId}`);
          return;
        }
        try {
          await createBotMetricClient(payloadWithName, activeToken);
          console.info(`[Metrics] ✅ write OK via axios fallback (${reason}) | session=${sessionId}`);
        } catch (err) {
          // Every transport failed — roll back the signature latch so a later lifecycle
          // event re-attempts this (and any newer) data instead of skipping it as "already sent".
          if (lastSentMetricsSigRef.current === interactionSig) lastSentMetricsSigRef.current = '';
          console.warn(`[Metrics] ❌ all transports FAILED (${reason}) | session=${sessionId}`, err);
        }
      })();
    },
    [
      assistantId,
      clicksPerPrompt,
      engagementTimeSeconds,
      promptClicks,
      questionsPerSession,
      uniqueQueries,
      webSessionId,
      elementClicks,
    ]
  );

  const finalizeMetricsRef = useRef(finalizeAndPostMetrics);
  useEffect(() => {
    finalizeMetricsRef.current = finalizeAndPostMetrics;
  }, [finalizeAndPostMetrics]);

  /**
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   */
  const wavRecorderRef = useRef<WavRecorder>(
      new WavRecorder({ sampleRate: 24000 })
    );
    const wavStreamPlayerRef = useRef<WavStreamPlayer>(
      new WavStreamPlayer({ sampleRate: 24000 })
    );
  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (!wavStreamPlayer) return;

    if (!wavStreamPlayer.context) {
      wavStreamPlayer.connect().then(() => {
        wavStreamPlayer.setOutputVolume?.(1);
      }).catch((err: any) => {
        console.warn('[IndexPageGuest] Failed to initialize audio output volume:', err?.message || err);
      });
      return;
    }

    wavStreamPlayer.setOutputVolume?.(1);
  }, []);

  const interruptPlayback = useCallback(async () => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (!wavStreamPlayer) return;
    try {
      await wavStreamPlayer.interrupt();
      logRelay('[IndexPageGuest] 🔈 Playback interrupted.');
    } catch (err) {
      console.warn('[IndexPageGuest] Failed to interrupt playback:', err);
    }
  }, []);

  const toggleMute = useCallback(async () => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (!wavStreamPlayer) return;
    if (!wavStreamPlayer.context) {
      try {
        await wavStreamPlayer.connect();
        wavStreamPlayer.setOutputVolume?.(1);
      } catch (err) {
        console.warn('[IndexPageGuest] Failed to initialize audio context for mute toggle:', err);
        return;
      }
    }
    const ctx = wavStreamPlayer.context;
    if (!ctx) return;
    try {
        if (ctx.state === 'running') {
          await ctx.suspend();
          setIsMuted(true);
          logRelay('[IndexPageGuest] 🔇 Speaker muted (Cmd+M).');
        } else {
          await ctx.resume();
          setIsMuted(false);
          logRelay('[IndexPageGuest] 🔊 Speaker unmuted (Cmd+M).');
        }
    } catch (err) {
      console.warn('[IndexPageGuest] Failed to toggle mute:', err);
    }
  }, []);

  /**
   * Set up render loops for the visualization canvas
   */
  //const clientCanvas = clientCanvasRef.current!;
  
  // Canvas Visualizations - using ChatUIMin //
    //logRelay('SelectedBot: ',selectedBot);
    useEffect(() => {
     
      let isLoaded = true;
    
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
    
      let videoContainer: HTMLElement | null = null;
    
      let clientCtx: CanvasRenderingContext2D | null = null;
      let serverCtx: CanvasRenderingContext2D | null = null;
    
      function render() {
        if (!isLoaded) return;
    
        const clientCanvas = clientCanvasRef.current;
        const serverCanvas = serverCanvasRef.current;
    
        if (!clientCanvas || !serverCanvas) return;
    
        if (!clientCanvas.width || !clientCanvas.height) {
          clientCanvas.width = clientCanvas.offsetWidth;
          clientCanvas.height = clientCanvas.offsetHeight;
        }
        clientCtx = clientCtx || clientCanvas.getContext('2d');
        if (clientCtx) {
          clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
          const result = wavRecorder.recording
            ? wavRecorder.getFrequencies('voice')
            : { values: new Float32Array([0]) };
          WavRenderer.drawBars(clientCanvas, clientCtx, result.values, '#0099ff', 10, 0, 8);
        }
    
        if (!serverCanvas.width || !serverCanvas.height) {
          serverCanvas.width = serverCanvas.offsetWidth;
          serverCanvas.height = serverCanvas.offsetHeight;
        }
        serverCtx = serverCtx || serverCanvas.getContext('2d');
        if (serverCtx) {
          serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
          const result = wavStreamPlayer.analyser
            ? wavStreamPlayer.getFrequencies('voice')
            : { values: new Float32Array([0]) };
    
          const intensity = Math.max(...result.values);
          let shadowSize = Math.min(intensity / 5, 550);
          shadowSize = Math.floor(shadowSize * 1000);
          if (shadowSize <= 30) shadowSize = 30;
    
          if (!videoContainer) {
            videoContainer = document.getElementById("video-container");
          }
          if (videoContainer) {
            videoContainer.style.boxShadow = `0 0 ${shadowSize}px green`;
          }
    
          WavRenderer.drawBars(serverCanvas, serverCtx, result.values, '#009900', 10, 0, 8);
        }
    
        requestAnimationFrame(render);
      }
    
      const waitForCanvas = () => {// ChatUIMin Canvas Loop and Guard
        const params = new URLSearchParams(window.location.search);
        const botId = params.get('id');
      
         if (botId === 'asst_RVOwZb5CDbjHbvIamdyMMRik') {
            if (!clientCanvasRef.current || !serverCanvasRef.current) {
            console.warn('⏳ Waiting for canvas refs...');
            setTimeout(waitForCanvas, 50);
            return;
          }
          logRelay('✅ Canvas refs mounted. Starting render loop');
          render();
       }
      };
    
      waitForCanvas(); // ✅ SAFE ENTRY POINT
    
      return () => {
        isLoaded = false;
      };
     
    }, []);
  
   // Canvas Visualizations //
  
  useEffect(() => {// Token Security - Updated for allowing time for token to process  
      const handleTokenUpdate = () => {
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('token='))
        ?.split('=')[1]; // Retrieve token value

      //logRelay('[Token Debug] Token found:', token ? '✅ Yes' : '❌ No');

      if (!token) {
        //console.warn('[Token Debug] No token found in cookies. Skipping redirect for now.');
        return; // Prevent immediate redirect
      }

      const tokenTimestamp = localStorage.getItem('tokenTimestamp');

      // ⛔ FIX 1 (2026-08-14) — THIS GATE HAS NO JURISDICTION OVER A GUEST SESSION.
      // Confirmed loop: a leftover AUTHED `token` cookie (set with no Max-Age ⇒ a session cookie
      // Chrome's session-restore keeps alive for weeks) plus a >24h `tokenTimestamp` made this
      // redirect fire on EVERY /ibot-guest load. `signInGuest` writes neither of those, so a guest
      // mint could never clear it — 20 successful mints, 20 bounces, ~0.7s apart. A guest's
      // validity is the MINT's concern; the authed timestamp must not speak for it.
      if (!authTimestampGateApplies({
        guestMode: localStorage.getItem('guestMode'),
        cookieToken: token,
        tokenTimestamp,
      })) {
        return;
      }

      if (isAuthTokenStale(tokenTimestamp)) {
        logRelay('🔄 Token expired - Redirecting to sign-in...');
        finalizeMetricsRef.current?.('token_expired');
        // ⛔ FIX 4 — BOUNDED. Even a correct redirect must not be able to spin forever; this is
        // the family fix, independent of the mechanism above.
        const { attempts, exceeded } = bumpGuestSignInAttempts(sessionStorage);
        if (exceeded) {
          logRelay(`⛔ Guest sign-in bounced ${attempts}x — stopping instead of looping`);
          setGuestLoopError(
            `Sign-in did not complete after ${attempts} attempts. The stored session looks stale — `
            + 'clearing site data for this page, or signing in again, should resolve it.',
          );
          return;
        }
        purgeStaleAuthKeys({ localStorage, sessionStorage, document }); // [S2-A] never carry the stale state into /signin
        window.location.href = getSignInUrl();
      } else {
        logRelay('✅ Token valid - Continuing session.');
      }
     };

      handleTokenUpdate();
      window.addEventListener('tokenUpdated', handleTokenUpdate);

      return () => window.removeEventListener('tokenUpdated', handleTokenUpdate);
 
  }, [finalizeMetricsRef, getSignInUrl]);
  //end secure token


  useEffect(() => {
    const handleWindowLifecycle = () => {
      // logRelay('[Metrics] handleWindowLifecycle triggered');
      try {
        localStorage.setItem('metrics_last_attempt', new Date().toISOString());
      } catch (err) {
        // console.warn('[Metrics] Failed to persist last_attempt:', err);
      }
      // Force metrics flush on close/navigation regardless of prior activity check
      finalizeAndPostMetrics('window_unload');

      // [MemoryRecall] Save current session summary for cross-session recall.
      // Uses THIS page's ref (set only when this bot has the MemoryRecall flag) — never a
      // shared global slot, which caused cross-bot memory bleed.
      try {
        const recallBotId = memoryRecallBotIdRef.current;
        if (recallBotId) {
          const currentItems = itemsRef.current || [];
          // Only save if session had meaningful content (at least 2 assistant messages)
          const assistantCount = currentItems.filter(
            (it: any) => it?.role === 'assistant' && (it?.formatted?.transcript || it?.formatted?.text)
          ).length;
          if (assistantCount >= 2) {
            const entry = generateCurrentSessionEntry(currentItems);
            if (entry) {
              appendMemoryRecallSession(recallBotId, entry);
              console.log(`[MemoryRecall] Session entry appended to rolling cache (${entry.snippet.length} chars)`);
            }
          }
        }
      } catch (recallErr: any) {
        console.warn('[MemoryRecall] Failed to save session summary on unload:', recallErr?.message);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Send immediately while the page is still active
        // logRelay('[Metrics] visibilitychange->hidden');
        finalizeAndPostMetrics('visibility_hidden');
        // Also schedule a very short follow-up to catch any missed path
        setTimeout(() => finalizeAndPostMetrics('visibility_hidden_timeout'), 20);
        // Fire a second backup after 150ms in case the first two are skipped
        setTimeout(() => finalizeAndPostMetrics('visibility_hidden_timeout2'), 150);
      }
    };

    window.addEventListener('beforeunload', handleWindowLifecycle);
    window.addEventListener('pagehide', handleWindowLifecycle);
    //window.addEventListener('unload', handleWindowLifecycle);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // logRelay('[Metrics] lifecycle listeners attached');

    return () => {
      window.removeEventListener('beforeunload', handleWindowLifecycle);
      window.removeEventListener('pagehide', handleWindowLifecycle);
      window.removeEventListener('unload', handleWindowLifecycle);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clicksPerPrompt, engagementTimeSeconds, finalizeAndPostMetrics, isConnected, questionsPerSession]);


useEffect(() => {// Ensure canvas contexts are available
  if (clientCanvasRef.current) {
    const ctx = clientCanvasRef.current.getContext('2d');
    if (ctx) {
      logRelay('🎨 Client canvas context ready:', ctx);
    } else {
      console.warn('❌ Client canvas context is null');
    }
  }

  if (serverCanvasRef.current) {
    const ctx = serverCanvasRef.current.getContext('2d');
    if (ctx) {
      logRelay('🎨 Server canvas context ready:', ctx);
    } else {
      console.warn('❌ Server canvas context is null');
    }
  }
}, []);

// INIT REALTIME
const navigate = useNavigate();
// [FIX useRef-anti-pattern] Lazy init. `useRef(new RealtimeClient(...))` evaluates the
// constructor on EVERY render and discards all but the first instance — combined with the
// 1Hz heartbeat re-render that produced a throwaway client (and a CREATED #N log line)
// every second, burying real client creations in the console. The guard below constructs
// exactly once; `clientRef.current` stays a non-null RealtimeClient for all consumers.
const clientRef = useRef<RealtimeClient>(null as unknown as RealtimeClient);
if (!clientRef.current) {
  // Initialize WebSocket Client to OpenAI Realtime API (default URL; real URL set on connect)
  clientRef.current = new RealtimeClient({
    url: 'wss://api.openai.com/v1/realtime',
  });
}

// [FIX double-client] True when the current client's ws is OPEN **or CONNECTING**.
// isConnected() alone is false while the socket is still CONNECTING — overwriting the
// ref in that window orphans the in-flight socket: the greeting still plays on the old
// client (its listeners are attached), but every later send hits the unconnected
// replacement ("WebSocket not connected or stale" ~seconds after a working greeting).
const isClientWsLive = () => {
  const _ws: any = (clientRef.current as any)?.realtime?.ws;
  return !!_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING);
};

let [iBots, setIBots] = useState<any[]>([]); // Stores the loaded iBot data

let currentThreadId: string | null = null;
let currentRunId: string | null = null;
const standbyStateRef = { firstSent: false, lastIndex: -1 };
const metricsHoldRef = { current: false };

const resolveBotNameById = (id: string | null) => {
  if (!id) return '';
  try {
    // [S2-B] the loaded record list (was the compiled iBot.json roster)
    const match = iBots
      ?.flatMap((g: any) => g.iBots)
      ?.find((b: any) => b?.botid === id);
    if (match) return match.name || '';
  } catch (err) {
    // ignore
  }
  return '';
};


/* INITIALIZATION */
useEffect(() => {// Init
  let cancelled = false;

  const runInit = async () => {
    // Reset metric counters for a fresh session view
    setClicksPerPrompt(0);
    setPromptClicks({});
    setUniqueQueries(0);
    uniqueQueriesSetRef.current.clear();
    setQuestionsPerSession(0);
    setEngagementTimeSeconds(0);
    setElementClicks({});
    sessionStartRef.current = null;
    metricsPostedRef.current = false;
    lastSentMetricsSigRef.current = '';
    metricsHoldRef.current = false;
    try {
      clearMetricQueue();
      localStorage.removeItem('last_metrics_payload');
      sessionStorage.removeItem('metricsSent');
      sessionStorage.removeItem('metricsHold');
    } catch (err) {
      // ignore storage issues
    }
    
    // Get assistantId from query string or default
    const searchId = searchParams.get('id');
    const storedId = localStorage.getItem("lastBotId");
    const storedSelectedBot = (() => {
      try {
        const raw = localStorage.getItem('SelectedBot');
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();

    // Get Prompt from Query String
    const qsPrompt = searchParams.get('prompt');
    if (qsPrompt && qsPrompt.trim() !== '') {
      logRelay('-> Query String Prompt Detected:', qsPrompt);
      localStorage.setItem('pendingPrompt', qsPrompt);
    }

    const qsSession = searchParams.get('ses');
    const qsContext = (searchParams.get('ctx') || 'openai').toLowerCase();
    if (qsSession) {
      setLinkedSessionId(qsSession);
      try {
        sessionStorage.setItem('webSessionId', qsSession);
      } catch {
        // ignore storage issues
      }
    }
    setContextSource(qsContext);

    /* DEFAULT ROOT BOT or Session Base Prompt */
    let id = searchId !== null && searchId !== '' ? searchId : (storedId || '');
    // [S2-B B2] Bare /ibot-guest (no ?id=, nothing stored): the HOST's record via GET
    // /agent-bootstrap?host= — the same rule as /signin, /guest and /ibot — else the explicit
    // "choose an assistant" state. ⛔ Never the Nova default this line used to carry (decision A).
    if (!id) {
      const boot: any = await fetchAgentBootstrap(
        { host: window.location.hostname },
        { baseUrl: (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '') || undefined },
      );
      if (cancelled) return;
      if (boot.status !== 'ok') {
        const shown = boot.status === 'not-found' ? 'choose' : 'unavailable';
        console.error(`[BotIdentity] bare /ibot-guest: host ${window.location.hostname} ${boot.status} — ${boot.reason}`);
        setStatusThinking(false);
        setItems([{ role: 'system', formatted: { text: (PREAUTH_TEXT as any)[shown] } }]);
        return;
      }
      id = bootstrapBotId(boot.record);
    }

    // If a new id was requested, clear any stale snapshot so old avatars/logos don't bleed through
    if (searchId && storedSelectedBot?.botid && storedSelectedBot.botid !== searchId) {
      clearSelectedBotSnapshot();
    }

    if (id) {
      localStorage.setItem('lastBotId', id);
    }

    // ✅ Check for session base prompt ID
    if (id === "pmpt_68d6adf9c1148195bf696618460b883301e26765bc41a937") {
      logRelay("[Init] Detected session base prompt ID, using fetchPromptSession instead of legacy loader.");

      const session = await fetchPromptSession(id);
      if (cancelled) return;

      if (session?.assistantId && session?.endpoint) {
        // ✅ Use promptId (pmpt_...) for assistantId so UI lookups work
        setAssistantId(id);
        assistantId = id;
        localStorage.setItem("selectedBot", assistantId ?? "");

        // ✅ Wrap session into iBot-compatible object
        // [S2-B] iBot.json import removed. This branch is dead-on-arrival and left for Phase 6:
        // fetchPromptSession sends `Bearer undefined` (openaiAPI.tsx, 79856a5) and throws above.
        const match: any = null;

        const sessionBot = {
          ...(match || {}),                // pull canned prompts, title, etc.
          botid: id,                       // keep pmpt_ id for lookup
          sessionId: session.assistantId,  // store sess_ id separately
          model: session.model || "gpt-realtime-mini",
          voice: session.voice || (match?.voice ?? "ash"),
          endpoint: session.endpoint,
        };

        selectedBot = sessionBot;
        const resolvedName = (sessionBot as any)?.name || resolveBotNameById(id);
        persistSelectedBot(sessionBot, resolvedName);

        logRelay("[Init] Session-based Assistant loaded (Guest):", sessionBot);

        // ✅ Initialize realtime client with session.endpoint
        if (isClientWsLive()) {
          console.log('[Init] Client ws live — skipping session-bot client re-creation (double-client guard).');
        } else {
          clientRef.current = new RealtimeClient({
            url: session.endpoint,
          });
        }

        // ✅ UI feedback
        setItems([{ role: "system", formatted: { text: "Session Assistant Loaded (Guest)" } }]);
      } else {
        console.error("[Init] Failed to load session assistant for prompt (Guest):", id);
        setItems([{ role: "system", formatted: { text: "❌ Failed to load session assistant (Guest)" } }]);
      }

      return; // ✅ Stop legacy path
    }

    // Default assistantId if not present
    setAssistantId(id);
    assistantId = id; // Set global variable
    localStorage.setItem('selectedBot', assistantId);
    logRelay('[Init] Set AssistantId:', assistantId);

    // [S2-A hotfix 2026-09-21 → S2-B] The "CleanUp Conversation" effect used to connect only if
    // runInit's prefix reached `selectedBot = bot` before any await (660be17 broke that: 75 s
    // stall on staging T1). It is now armed by `botReady`, bumped ONCE after the real client is
    // created, so the record fetch below may await. s2a-autoconnect-shape.test.js pins the arming.

    // Set initial items
    setItems([{ role: "system", formatted: { text: "LOADING ASSISTANT..." } }]);
    setStatusThinking(true);
    logRelay('[Init] Initial items set:', items);

    // [S2-B] ONE path for EVERY id shape (asst_ / agent_ / ObjectId) — the `isRagBot` split and
    // the compiled iBot.json match are gone. selectedBot is built from the DB record by the shared
    // builder (src/utils/botIdentity.js, S2 note §3.1). No JSON fallback (decision A): if botapi
    // cannot produce the record the page renders the unavailable state and stops.
    // BotSettings first — botapi resolves any id shape to the agent server-side — then the
    // assistants record under the agent id it names (session fields: name/model/voice/tools).
    // The connect effect is armed by `botReady` (one bump, after the real client is created
    // below), so this await no longer races the mount-time effect the way 660be17's did.
    logRelay('[Init] Loading bot record from botFactoryAPI:', id);

    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('token='))
      ?.split('=')[1] || localStorage.getItem('token') || '';

    // The unified failure state (decision A) — same on both pages; the reason goes to the console.
    // Never bumps botReady, so the connect effect stays disarmed.
    const failBotUnavailable = (reason: string) => {
      const state = botUnavailableState(id, reason);
      console.error(state.log);
      selectedBot = null;
      selectedHostId = null;
      clearSelectedBotSnapshot();
      setStatusThinking(false);
      setItems(state.items);
    };

    const fetchErrors: string[] = [];
    const settingsData = await getBotSettings(id, token).catch((err: any) => {
      fetchErrors.push(`botsettings: ${err?.statusCode ?? ''} ${err?.message ?? err}`.trim());
      return null;
    });
    if (cancelled) return;
    const recordAgentId = settingsData?.agentId || id;
    const [assistantData, legacySettings, configData] = await Promise.all([
      getAssistant(recordAgentId, token).catch((err: any) => {
        fetchErrors.push(`assistants: ${err?.message ?? err}`);
        return null;
      }),
      getAssistantSettings(recordAgentId, token).catch(() => null),
      getAssistantConfig(recordAgentId, token).catch(() => null),
    ]);
    if (cancelled) return;
    logRelay('[Init] Bot record loaded from API:', { settingsData, assistantData, configData });

    const built = buildSelectedBotFromRecord(assistantData, settingsData, configData, { requestedId: id });
    if (!built.ok) {
      failBotUnavailable([built.reason, ...fetchErrors].join('; '));
      return;
    }
    selectedBot = built.bot;
    selectedHostId = built.hostId;
    logRelay('[INITIALIZATION] Loaded Selected Bot:', selectedBot);

    // Persist branding for Header/ChatUI compatibility (settings.json shape). ⚠️ Header.tsx still
    // overwrites this from settings.json when a disk match exists — that is S2-B phase 2 (B2).
    // B1c: branding-only, allowlisted; never the record. Without a session /botsettings 401s and
    // only the (unauthenticated) legacy /setting response is in hand — its branding is used, the
    // rest of the document is not. No branding anywhere ⇒ store nothing, say so.
    const { payload: botSettingsPayload, source: brandingSource } =
      buildBotSettingsPayload(selectedBot, settingsData, legacySettings);
    if (!settingsData) {
      console.warn(`[BotIdentity] /botsettings unavailable (${fetchErrors.join('; ') || 'no record'}) — branding from ${brandingSource || 'nowhere'}`);
    }
    if (!botSettingsPayload) {
      console.warn('[BotIdentity] no branding on the record — botSettings not stored');
    } else {
      try {
        storeBotSettings(botSettingsPayload); // writes, then tells Header to re-read (same load)
      } catch (err) {
        console.warn('[Init] Failed to persist botSettings:', err);
      }
    }

    // Downstream lookups (sendTextQuery & co.) find the bot by botid in this list.
    setIBots([{ iBots: [selectedBot] }]);
    iBots = [{ iBots: [selectedBot] }];
 
    persistSelectedBot(selectedBot, (selectedBot as any)?.name);

    selectedVoice = selectedBot.voice;
      
    const endpoint = selectedBot?.endpoint || "https://api.openai.com/v1/chat/completions"; // Extract endpoint
    logRelay(`[INITIALIZATION] Detected Endpoint: ${endpoint}`);

    // COMPLETION NAV
    if (endpoint === "wss://api.openai.com/v1/chat/completions") {
      
      logRelay("[Debug] Completions endpoint detected. Navigating to /chat...");
      navigate(`/chat/?id=${assistantId}`, {
        state: { selectedBot },
      });
    return; // Stop further real-time setup

    }

    if (selectedBot?.title) {
      logRelay('[✅ - IndexPage] Updating title...');

      // Set the document title to the selected bot's title
      let welcomeTitle ="Bot Not Loaded"

      if(["asst_SRqG7nOQ8boguG6EeEPkpNlE", "asst_k6gkeaM6VZsbvOjfBNt6BFrK", "asst_LaKCs3TTtYdMDI8AAHkFwrKj"].includes(selectedBot.id)){
        welcomeTitle= "Build on Our Success"
      }
      document.title = selectedBot.title || welcomeTitle;

    }
      
    
    // Update WebSocket URL to include the endpoint
    const wsSes = webSessionId ? `&ses=${encodeURIComponent(webSessionId)}` : '';
    // [S2-B §3.2] Host id from the record: legacyAssistantId || id (asst_ while the Assistants API
    // lives; a native agent_ sends its own id and the relay resolves it). No substitution — the
    // Nova fallback that made every agent_* link speak as Nova is deleted. Empty ⇒ hard fail.
    const wsHostId = selectedHostId || '';
    if (!wsHostId) {
      failBotUnavailable('empty host id at socket build');
      return;
    }
    const wsVoice = selectedBot.voice ? `&voice=${encodeURIComponent(selectedBot.voice)}` : '';
    // [MemoryRecall] Include prior-session summary in URL metadata so the relay receives it
    // even if connectConversation() fires before startupMemoryRecall() resolves (race condition).
    // buildMemoryMetadataPayload reads localStorage synchronously — returns prior session value.
    const _recallBotId = (selectedBot as any)?.botid || assistantId;
    const _priorSummary = (selectedBot as any)?.MemoryRecall ? buildMemoryMetadataPayload(_recallBotId) : null;
    // ⛔ 1c6c145's OWN wsMemory / wsUrl / RealtimeClient lines are deliberately NOT taken here.
    // They are SUPERSEDED by 2b29e1e (C7 unit 3), which is already on this branch below: it
    // builds the FULL metadata (identity + memory) before the URL — the fix for the silent
    // guest-chatHistory loss this very commit caused — and creates the client behind the
    // double-client guard below. Taking them would re-introduce the {memorySummary}-only URL.

    // [S2-A Note 8] Mint-time org is not session-lifetime org. If this page's bot is not the
    // bot the org was resolved for (a ?id= change, a Header bot switch → new /ibot-guest?id=),
    // re-resolve from BotSettings BEFORE the metadata below reads localStorage.orgId. No-op
    // for the mint-time bot; never throws. Runs after selectedBot is built and before the ONE
    // metadata build below (S2-B: the API path's twin re-resolve went with the split); the
    // client below is connected by the botReady-armed effect.
    await reResolveGuestOrg(id);
    if (cancelled) return;

    // [ChatHistory FIX 2026-06-20] This legacy-bot path previously put ONLY { memorySummary }
    // on the WS URL, dropping userid/org. The relay's persist guard then skipped every write
    // (this.userId/this.orgId arrived undefined) — silently killing guest chatHistory since the
    // 2026-06-10 memory-recall change. Build the FULL metadata (identity + memory) HERE, before
    // the URL, and send it on the URL exactly like the RAG/realtime path does.
    const metadataUser = readStoredUser() || user;
    const orgIdCandidate =
      localStorage.getItem('orgId') ||
      (typeof metadataUser?.org === 'object'
        ? metadataUser?.org?.orgId || metadataUser?.org?.id
        : undefined) ||
      undefined;
    const resolvedUserId =
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('userId='))?.split('=')[1] ||
      localStorage.getItem('userId') ||
      '';
    const storedThreadId = localStorage.getItem('currentThreadId');
    const storedRunId = localStorage.getItem('currentRunId');
    const resolvedThreadId = (() => {
      try {
        return storedThreadId ? JSON.parse(storedThreadId) : '';
      } catch {
        return storedThreadId || '';
      }
    })();
    const resolvedRunId = (() => {
      try {
        return storedRunId ? JSON.parse(storedRunId) : '';
      } catch {
        return storedRunId || '';
      }
    })();
    const metadata: Record<string, any> = {
      id: selectedBot.id,
      botid: selectedBot.botid,
      name: selectedBot.name,
      title: selectedBot.title,
      model: selectedBot.model,
      endpoint: selectedBot.endpoint,
      voice: selectedBot.voice,
      userid: resolvedUserId,
      org: { orgId: orgIdCandidate || '' },
      threadId: resolvedThreadId,
      runId: resolvedRunId,
      language: getLanguageMode((selectedBot as any)?.botid) || 'English',
    };
    // [MemoryRecall] Include prior-session summary in full metadata (used by second client path).
    if (_priorSummary) metadata.memorySummary = _priorSummary;

    const wsMemory = `&metadata=${encodeURIComponent(JSON.stringify(metadata))}`;
    // ⛔ TWO ADAPTATIONS on this line:
    //  1. `wsHostId` replaces main's inline ternary — same value, but it is the variable the
    //     [BotIdentity] substitution diagnostic above computes and reports on.
    //  2. `${wsVoice}` RESTORED at C5 — it belongs to 1c6c145 and was dropped at C7 only
    //     because it arrived as conflict contamination in 2b29e1e's hunk. Term order now
    //     matches origin/main: ...${wsSes}${wsVoice}${wsMemory}.
    const wsUrl = `${RELAY_SERVER_URL}?id=${wsHostId}&endpoint=${encodeURIComponent(endpoint)}${wsSes}${wsVoice}${wsMemory}`;
    //gpt-5 - dont pass endpoint
    //const wsUrl = `${RELAY_SERVER_URL}?id=${assistantId}`;

    // [FIX double-client] Same guard as the second creation site below: if a previous
    // runInit pass created a client and connectConversation() already connected it,
    // overwriting it here orphans the LIVE WebSocket — the greeting still flows on the
    // old client, but every later mic/send call hits the fresh UNCONNECTED replacement
    // ("WebSocket is not connected" on startRecording).
    if (clientRef.current?.isConnected?.() || isClientWsLive()) {
      console.log('[Init] Client ws live (OPEN/CONNECTING) — skipping early client re-creation (double-client guard).');
    } else {
      clientRef.current = new RealtimeClient({ url: wsUrl }); // Pass the updated URL to RealtimeClient
      // Arm the "CleanUp Conversation" effect (the file path's ONLY auto-connect). Pre-S2-A it
      // connected this client because the mount flush reached this line before any await; the
      // Note-8 await above now precedes it, so the effect must be told the real client exists.
      // NOT a second connectConversation() — the same effect, fired once per created client.
      if (!cancelled) setBotReady((n) => n + 1);
    }
    //logRelay(`✅ - [Update] WebSocket URL: ${wsUrl}`);
    
    //const selectedPrompt = localStorage.getItem("selectedPrompt")|| "TO DO - Get Text Prompt from Chat UI";
      

    // [ChatHistory FIX] metadata (identity + memory) is now built above, before the WS URL,
    // so the URL carries userid/org. Persist it here for Header/ChatUI compatibility.
    localStorage.setItem('metadata', JSON.stringify(metadata));
    logRelay('✅ - [Metadata] local storage payload (now ALSO sent to WS):', metadata);

    // [LanguageMode] Sync relay cookie to THIS bot's language right before connecting.
    const guestPreBotLang2 = metadata.language || 'English';
    document.cookie = [
      `ibot_language_mode=${encodeURIComponent(guestPreBotLang2)}`,
      'path=/',
      'SameSite=Lax',
      location.protocol === 'https:' ? 'Secure' : '',
      'max-age=31536000',
    ].filter(Boolean).join('; ');

    // [MemoryRecall] Single canonical startup call — same function used by all bot types.
    if ((selectedBot as any)?.MemoryRecall) {
      memoryRecallBotIdRef.current = (selectedBot as any)?.botid || assistantId;
      await startupMemoryRecall(
        (selectedBot as any)?.botid || assistantId,
        resolvedUserId,
        resolvedRunId || null
      );
      // After API fetch, refresh memorySummary in metadata in case startupMemoryRecall
      // stored a newer summary (e.g. from Robot Factory for authenticated users).
      const freshSummary = buildMemoryMetadataPayload(_recallBotId);
      if (freshSummary) metadata.memorySummary = freshSummary;
    }

    if (selectedBot) {
      if (!RELAY_SERVER_URL) {
        console.error('[Init] Missing RELAY_SERVER_URL; cannot initialize realtime client.');
        setItems([{ role: 'system', formatted: { text: '❌ Missing relay server URL' } }]);
        return;
      }

      const endpoint = selectedBot.endpoint;
      logRelay('[Endpoint Debug] Detected Endpoint:', endpoint);

      // Handle Visualization and Mic Display
      const isRealtimeMode = endpoint.includes('realtime');

      // Handle Visualization Display
      const visualizationElements = document.getElementsByClassName("content-visuals");
      Array.from(visualizationElements).forEach((element) => {
        const div = element as HTMLDivElement;
        if (endpoint.includes("realtime")) {
          div.style.display = "inline-block"; // Show in realtime mode
        } else {
          div.style.display = "none"; // Hide in completions or unsupported modes
        }
        logRelay(`[Visuals Debug] Visualization display updated: ${div.style.display}`);
      });

      const micButton = document.getElementById("toggle-voice") as HTMLButtonElement;

      if (micButton) {
        micButton.disabled = !isRealtimeMode; // Enable mic button in realtime mode
        micButton.style.display = isRealtimeMode ? "inline-block" : "none"; // Show/hide mic button

        //miclabel.disabled = !isRealtimeMode; // Enable mic miclabel in realtime mode
        //miclabel.style.display = isRealtimeMode ? 'inline-block' : 'none'; // Show mic miclabel in realtime mode

        logRelay(`[microphone Debug] Mic button display updated: ${micButton.style.display}`);
      }

      if (endpoint.includes('realtime')) {
        // [FIX double-client] Guard: if the CleanUp useEffect already called connectConversation()
        // while runInit was suspended on await startupMemoryRecall(), clientRef.current is already
        // connected. Re-creating it here would overwrite the live client with a fresh unconnected
        // one, causing every sendTextQuery to see isConnected()=false and trigger a reconnect
        // (new session + new greeting). Skip re-creation if client is already connected.
        if (clientRef.current?.isConnected?.() || isClientWsLive()) {
          console.log('[Init] Client ws live (MemoryRecall async race) — skipping client re-creation to preserve live session.');
        } else {
          logRelay('[realtime Debug] Realtime endpoint detected. Initializing WebSocket client.');
          clientRef.current = new RealtimeClient({
            url: (() => {
              const baseUrl = new URL(RELAY_SERVER_URL); // Parse base WebSocket URL
              const assistantId = id;

              if (assistantId) {
                // Update the search string (query parameters)
                const params = new URLSearchParams(baseUrl.search);
                params.append('id', assistantId);
                params.append('endpoint', endpoint); // Append the endpoint to the query string
                if (webSessionId) {
                  params.append('ses', webSessionId);
                }

                // NOT for GPT-5 Append the model and metadata parameters in the correct order
                //params.append('model', 'gpt-realtime'); // <--- HEY Add the model first
                if (metadata && typeof metadata === 'object') {
                  params.append('metadata', JSON.stringify(metadata)); // Add the metadata without encoding

                }
                baseUrl.search = params.toString(); // Set the updated search string

                // Ensure the `path` is properly formed
                const path = `/${baseUrl.search}`;
                return `${baseUrl.origin}${path}`; // Construct the full WebSocket URL
              }

              return baseUrl.toString(); // Default case if no assistantId
            })(),
          });
        }
      } else if (endpoint === 'https://api.openai.com/v1/chat/completions') {
        logRelay('[Step 2 Debug] HTTPS endpoint detected. No WebSocket client initialized.');
        // Add HTTPS-specific logic here if needed
      } else {
        console.warn('[Step 2 Debug] Unsupported endpoint protocol:', endpoint);
      }
    } else {
      console.warn('[INI Debug] No matching bot found for assistantId:', assistantId);
    }
  };

  runInit();

  return () => {
    cancelled = true;
  };
}, [searchParams]);

 

// Inject JSON-LD for the assistant after component mounts
useEffect(() => {
injectAssistantJsonLd();
}, [assistantId]); // Re-run when assistantId changes


  const safeSend = async (message: string) => {
    if (sessionExpiredRef.current) return;
    const client = clientRef?.current;
    if (!client) {
      console.warn("[safeSend] clientRef is undefined");
      return;
    }

    try {
    if (!client.isConnected()) {
        if (sessionExpiredRef.current) return;
        console.warn('[sendTextQuery] WebSocket not connected. Reconnecting...');
        try {
          await client.connect();
          logRelay('[sendTextQuery] Reconnected successfully.');
        } catch (err) {
          console.error('[sendTextQuery] Failed to reconnect:', err);
        }
      }

      client.sendUserMessageContent([{ type: 'input_text', text: message }]);
      logRelay("[safeSend] Message sent:", message);
    } catch (error: any) {
      console.warn("[safeSend] Failed to send message:", error.message);
    }
  };
  
    const sendHeartbeat = useCallback(() => {
    if (sessionExpiredRef.current) return false;
    const client = clientRef.current;
    const ws = client?.realtime?.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    const payload = { type: 'client.heartbeat', timestamp: Date.now() };
    try {
      ws.send(JSON.stringify(payload));
      // [DIAG idle-timing] The heartbeat bypasses RealtimeTransport.send, so stamp lastSendAt
      // here too — otherwise the WS-close "sinceLastSend" reading ignores keepalives.
      client!.realtime.lastSendAt = Date.now();
      console.log(`[IndexPage][Ping] heartbeat sent | serial=#${(client as any)?._serial} at ${new Date().toISOString()}`);
      return true;
    } catch (err) {
      console.warn('[IndexPage][Ping] failed heartbeat:', err);
      return false;
    }
  }, []);


  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  useEffect(() => {
    const client = clientRef.current;
    const sesToken = linkedSessionId || webSessionId;
    if (!client || !sesToken || contextHydratedRef.current) return;

    const ws = (client as any)?.realtime?.ws;
    if (!client.isConnected?.() || !ws || ws.readyState !== WebSocket.OPEN) return;

    // [ContextHydration] Guests cannot authenticate against /api/ws-session transcripts —
    // the fetch always 401s. Skip cleanly instead of logging a failed request.
    if (localStorage.getItem('guestMode') === 'true') return;

    const hydrateContext = async () => {
      try {
        let contextPrompt = '';
        if (contextSource === 'openai') {
          const resp = await fetch(
            `/api/ws-session/${encodeURIComponent(sesToken)}/transcript`
          );
          if (!resp.ok) {
            console.warn(`[ContextHydration] Transcript fetch failed: ${resp.status}`);
            return;
          }
          const payload = await resp.json();
          const rawMessages =
            (Array.isArray(payload?.data?.messages) && payload.data.messages.length
              ? payload.data.messages
              : payload?.data?.assistantMessages) || [];

          const ordered = rawMessages
            .map((m: any) => ({
              role: m?.role || 'assistant',
              time: m?.time || m?.timestamp || '',
              text: m?.text || m?.transcript || m?.message?.transcript || '',
              tokens: m?.tokens || m?.message?.tokens || {},
            }))
            .filter((m: any) => m.text);

          ordered.sort((a: any, b: any) => {
            const at = new Date(a.time || 0).getTime();
            const bt = new Date(b.time || 0).getTime();
            return at - bt;
          });

          if (ordered.length) {
            const lines = ordered.map(
              (m: any) => `${(m.role || 'assistant').toUpperCase()}: ${m.text}`
            );
            contextPrompt = [
              'Here is the full transcript of your previous session; continue the conversation naturally according to your SCB.',
              '',
              lines.join('\n'),
            ].join('\n');
          }
        } else if (contextSource === 'rag') {
          logRelay('[ContextHydration] ctx=rag selected; RAG hydration not wired yet.');
        }

        if (contextPrompt) {
          client.sendUserMessageContent([{ type: 'input_text', text: contextPrompt }]);
          contextHydratedRef.current = true;
        }
      } catch (err) {
        console.warn('[ContextHydration] Failed to hydrate context:', err);
      }
    };

    hydrateContext();
  }, [contextSource, linkedSessionId, webSessionId, searchParams, isConnected]);

/**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    // [PTT] Step 8: Only allow VAD toggle in MT mode — PTT has its own turn_detection config
    if (micModeRef.current === 'ptt') {
      logRelay('[PTT] changeTurnEndType blocked — PTT mode manages turn_detection');
      return;
    }
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
    setCanPushToTalk(value === 'none');
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (sessionExpiredRef.current || isSessionExpiredLocked()) return;
      if (document.visibilityState === 'visible') {
        const sent = sendHeartbeat();
        if (sent) {
          window.resetPingTimer?.();
          logRelay('[IndexPage][Ping] Visibility heartbeat');
        }
      } else {
        // [PTT] Step 17: Stop recording on page hide to release mic and commit audio
        if (isRecordingRef.current) {
          logRelay('[PTT] Page hidden — auto-stopping recording');
          stopRecording(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sendHeartbeat]);

  useEffect(() => {
    if (sessionExpiredRef.current || isSessionExpiredLocked()) return;
    if (typeof Worker === 'undefined') return;
    const workerCode = `
      const interval = ${HEARTBEAT_PING};
      setInterval(() => postMessage('tick'), interval);
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);
    heartbeatWorkerRef.current = worker;
    worker.onmessage = () => {
      if (sessionExpiredRef.current || isSessionExpiredLocked()) return;
      const sent = sendHeartbeat();
      if (sent) {
        window.__ping.pingTimestamp = Date.now();
        window.__ping.connectionAttempts += 1;
        logRelay('[IndexPage][Ping] Worker heartbeat tick');
      }
    };
    const sent = sendHeartbeat();
    if (sent) {
      window.__ping.pingTimestamp = Date.now();
      window.__ping.connectionAttempts += 1;
    }
    return () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  }, [sendHeartbeat]);

  /**
   * Utility to auto-scroll the event logs
   */
  useEffect(() => {// Auto Scroll
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      eventsEl.scrollTop = eventsEl.scrollHeight;
    }
  }, [realtimeEvents]);

  useEffect(() => {
    const handleElementClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a');
      const inConversation = !!target.closest('#conversation');
      const setHold = () => {
        try {
          metricsHoldRef.current = true;
          sessionStorage.setItem('metricsHold', '1');
          setTimeout(() => {
            metricsHoldRef.current = false;
            sessionStorage.removeItem('metricsHold');
          }, 4000);
        } catch (err) {
          // ignore
        }
      };
      // Stop button should not trigger metrics teardown
      if (target.closest('#stop-voice')) {
        setHold();
      }
      // Any link/button that may navigate: hold metrics to prevent unload post
      if (anchor) {
        setHold();
      }
      if (target.tagName.toLowerCase() === 'button' && inConversation) {
        setHold();
      }
      // Specific: email toolbar buttons/icons should not trigger teardown
      if (target.closest('.email-content-button')) {
        setHold();
      }
      if (target.closest('.card') || target.closest('.card-white')) return; // exclude prompt/card clicks from this bucket

      // Only track conversation-area clicks that have explicit media/data names
      if (inConversation) {
        const labeledAncestor = target.closest('[data-name]');
        const labeledName = labeledAncestor?.getAttribute('data-name') || '';
        if (!labeledName || !labeledName.startsWith('chat-')) {
          return;
        }
        const nowIso = new Date().toISOString();
        setElementClicks((prev) => {
          const current = prev[labeledName] || { count: 0, lastClicked: nowIso };
          return { ...prev, [labeledName]: { count: current.count + 1, lastClicked: nowIso } };
        });
        return;
      }

      const text = (target.textContent || '').trim().replace(/\s+/g, ' ');
      let name =
        target.getAttribute('data-label')?.trim() ||
        target.getAttribute('data-name')?.trim() ||
        target.getAttribute('aria-label')?.trim() ||
        target.getAttribute('title')?.trim() ||
        target.getAttribute('mediaAttributes.title')?.trim() ||
        (text ? text.slice(0, 80) : '');

      if (!name) {
        const cls = target.className && typeof target.className === 'string' ? target.className.trim() : '';
        const id = target.id?.trim();
        const fallbackText =
          text ||
          (target.closest('a')?.textContent || '').trim().replace(/\s+/g, ' ');
        if (fallbackText) {
          name = fallbackText.slice(0, 80);
        } else if (cls) {
          name = cls.split(/\s+/)[0].replace(/[-_]+/g, ' ');
        } else if (id) {
          name = id.replace(/[-_]+/g, ' ');
        } else {
          name = target.tagName.toLowerCase();
        }
      }

      // Persist a readable label for future clicks
      try {
        target.setAttribute('data-name', name);
        target.setAttribute('data-label', name);
      } catch (err) {
        // ignore
      }

      const nowIso = new Date().toISOString();
      setElementClicks((prev) => {
        const next = { ...prev };
        const current = next[name] || { count: 0, lastClicked: nowIso };
        next[name] = { count: current.count + 1, lastClicked: nowIso };
        return next;
      });
    };

    document.addEventListener('click', handleElementClick, true);
    return () => document.removeEventListener('click', handleElementClick, true);
  }, []);

  /* Keep Session Alive by Resetting Ping */
    useEffect(() => {// Session Keep Alive
      const resetPingOnUserActivity = () => {
        if (typeof window.resetPingTimer === 'function') {
          window.resetPingTimer();
        }
      };
    
      document.addEventListener('keydown', resetPingOnUserActivity);
      document.addEventListener('mousedown', resetPingOnUserActivity);
    
      return () => {
        document.removeEventListener('keydown', resetPingOnUserActivity);
        document.removeEventListener('mousedown', resetPingOnUserActivity);
      };
    }, []);
  
      /** Heartbeat Countdown Timer **/
      useEffect(() => {// Heartbeat Timer
        let countdown: NodeJS.Timeout;
      
        if (isConnected) {
          countdown = setInterval(() => {
            setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
          }, 1000);
        }
      
        return () => {
          clearInterval(countdown);
        };
      }, [isConnected]);
 
    const sendTextQuery = async (text: string, aiMessage?: string) => {
      logRelay(`**** sendTextQuery before`);
      const selectedBot = iBots
        .flatMap((group) => group.iBots)
        .find((bot) => bot.botid === assistantId);

      if (!selectedBot) {
        console.warn('No matching bot found for assistantId:', assistantId);
        return;
      }
      const client = clientRef.current;

      /* clientRef.corrent object holds the full session <data value="" className=""></data>
      path to conversation...role:
      clientRef.current.conversation.items[0].role
      On completed, the conversation array holds all the parameters in the current session.
      */

      // ✅ Extract `data-label` value from the clicked card
      let userMessage = text; // Default to text parameter
      const cleanMessage = userMessage
        .replace(/<\/?[^>]+(>|$)/g, "")
        .trim();

      const relayMessage = (aiMessage || text)
        .replace(/<\/?[^>]+(>|$)/g, "")
        .trim();

      logRelay('[Debug] Sending message to Relay:', relayMessage, 'Displaying in UI:', cleanMessage);

      if (!cleanMessage) {
        console.warn('No valid message found in data-label attribute or input field.');
        return;
      }

      setTimeLeft(Math.min(resetTimeout, PAGE_TIMEOUT)); // Reset timer

      try {
        // Ensure `data-label` text is displayed in the conversation   
        setItems((prevItems) => [  
          ...prevItems,
          { role: 'user', formatted: { text: cleanMessage } }, // 🚀 NOW using cleaned message
        ]
      );

      const inputElement = document.getElementById('user-input') as HTMLTextAreaElement;
      if (inputElement) {
        inputElement.value = ''; // Clear input field
      }




        /* ✅ SEND USER INPUT */
        if (!client.isConnected() || client.realtime.ws?.readyState !== WebSocket.OPEN) {
          if (sessionExpiredRef.current) return;
          const _ws = client?.realtime?.ws;
          // NOTE: expanded inline (not as an object) so collapsed console objects in
          // pasted logs never hide the values again.
          console.warn(
            `[sendTextQuery] WebSocket not connected or stale. Reconnecting... | ` +
            `clientSerial=#${(client as any)?._serial} refSerial=#${(clientRef.current as any)?._serial} ` +
            `isConnected=${client.isConnected()} wsExists=${!!_ws} wsReadyState=${_ws?.readyState}`
          );

          try {
            await client.connect();
            logRelay('[sendTextQuery] Reconnected successfully.');
          } catch (err) {
            // [FIX stale-race] If connect() throws "Already connected", a concurrent path
            // (e.g. attemptRestore) restored the connection between our stale check and
            // the connect() call. Safe to proceed — don't drop the message.
            if ((err as Error)?.message === 'Already connected') {
              logRelay('[sendTextQuery] Race resolved — already connected, proceeding with send.');
            } else {
              console.error('[sendTextQuery] Failed to reconnect:', err);
              return; // Prevent sending if reconnection genuinely fails
            }
          }
        }

      console.log(`[sendTextQuery] ✅ Sending to relay | text="${relayMessage?.slice(0, 80)}" | wsState=${(client.realtime.ws as any)?.readyState}`);
      client.sendUserMessageContent([{ type: 'input_text', text: relayMessage }]);
      console.log(`[sendTextQuery] ✅ Send OK`);


      } catch (error) {
        console.error('[IndexPageGuest] Error sending WebSocket query:', error);
      }
    logRelay(`**** sendTextQuery after`);
    };

  
  /**
   * Start recording
   */
    const startRecording = async (opts: { interrupt?: boolean } = {}) => {
      const selectedBot = iBots
        .flatMap((group) => group.iBots)
        .find((bot) => bot.botid === assistantId);

      const isRealtimeMode = selectedBot?.endpoint.includes('realtime');

      if (!isRealtimeMode) {
        console.warn('Recording disabled for non-realtime endpoints.');
        return;
      }

      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;


      if (isRecording) {
        logRelay('[Mic] Already recording — skipping init.');
        return;
      }

      logRelay(`[${micModeRef.current === 'ptt' ? 'PTT' : 'HF'}] startRecording`);

      try {
        logRelay('[IndexPage] Initializing wavRecorder...');
        const currentStream = wavRecorder.stream as MediaStream | null;
        const hasLiveAudioTrack =
          !!currentStream?.getAudioTracks?.().some((track) => track.readyState === 'live');
        if (currentStream && !hasLiveAudioTrack) {
          logRelay('[Mic] Existing stream has no live audio track; reinitializing recorder.');
          try {
            await wavRecorder.end();
          } catch (err) {
            console.warn('[Mic] Recorder end() failed during reinit:', err);
          }
        }
        if (!wavRecorder.stream && !wavRecorder.recording) {
          await wavRecorder.begin(); // ✅ Safe check prevents crash
        }

        logRelay('[IndexPage] wavRecorder initialized.');
        if (wavRecorder.stream) {
          mediaStreamRef.current = wavRecorder.stream as MediaStream;
        }

        // Hands-free keeps the mic live while the assistant talks, so it must not interrupt;
        // Push-to-Talk interrupts the assistant the moment the user starts to talk.
        if (opts.interrupt !== false) {
          const wavStreamPlayer = wavStreamPlayerRef.current;
          const trackOffset = await wavStreamPlayer.interrupt(); // 🔇 Stop assistant speaker if active
          // [FIX mic-bug-01] Only cancel if the assistant was actually mid-response.
          // Sending cancel when nothing is active triggers a response.done(cancelled) on the
          // relay, which flushes _pendingResponseCreate and causes a phantom empty response.
          if (trackOffset?.trackId) {
            client.cancelResponse("");
          }
        }

        await wavRecorder.record((data) => {
          // Hands-free: no microphone audio while typing or clicking (their noise becomes phantom turns).
          if (client.isConnected() && !(micModeRef.current === 'hf' && isInteractionNoise())) {
            client.appendInputAudio(data.mono);
          }

          // Input level (0 to 1) for the bar: root mean square of the 16-bit samples.
          let sumSquares = 0;
          for (let i = 0; i < data.mono.length; i++) {
            const v = data.mono[i] / 0x8000;
            sumSquares += v * v;
          }
          inputLevelRef.current = Math.min(1, Math.sqrt(sumSquares / (data.mono.length || 1)) * 4);

          const canvas = clientCanvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              try {
                const floatData = new Float32Array(data.mono.length);
                for (let i = 0; i < data.mono.length; i++) {
                  floatData[i] = data.mono[i] / 0x8000;
                }
                WavRenderer.drawBars(canvas, ctx, floatData, '#0f0', 48, 2, 1, true);
              } catch (err) {
                console.warn('❌ Error drawing client waveform:', err);
              }
            }
          }
        });

        recordingStartTimeRef.current = Date.now(); // [PTT] Stamp start time
        setIsRecording(true);
        setMicBlocked(false);
        logRelay('[🎙️IndexPage] Recording started.');
      } catch (error) {
        console.error('❌ [IndexPage] Error starting recording:', error);
        setIsRecording(false);
        inputLevelRef.current = 0;
        setMicBlocked(true); // permission denied or no input device — the bar shows the blocked look
        logRelay("❌ 🎙️ Microphone access stopped. Please check browser permissions and try again.");
      }
    };

  
    /**
     * Stop recording IndexPage
     */
    const stopRecording = async (force?: boolean) => {
      const selectedBot = iBots
        .flatMap((group) => group.iBots)
        .find((bot) => bot.botid === assistantId);
    
      const isRealtimeMode = selectedBot?.endpoint.includes('realtime');
    
      if (!isRealtimeMode) {
        console.warn('Recording is not active for non-realtime endpoints.');
        return;
      }
    
      const recordingDuration = Date.now() - recordingStartTimeRef.current;
      logRelay(`[${micModeRef.current === 'ptt' ? 'PTT' : 'HF'}] stopRecording | duration=${recordingDuration}ms`);

      // Hands-free: stopping is a mute (or a page-hidden release). No commit and no response
      // request: the platform detects the end of each turn on its own.
      if (micModeRef.current === 'hf') {
        setIsRecording(false);
        inputLevelRef.current = 0;
        try {
          await wavRecorderRef.current.pause();
          if (force) {
            mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
          }
        } catch (error) {
          console.warn('[MicMode] Error releasing the hands-free mic:', error);
        }
        return;
      }

      // [DUAL MODE] Quick tap (< 300ms) in PTT mode = tap to talk (keep mic open).
      // The user clicked briefly, so treat it as a toggle-on. They'll click again to stop.
      // Don't pause wavRecorder, don't set isRecording(false) — just return.
      if (!force && micModeRef.current === 'ptt' && recordingDuration < 300) {
        logRelay('[PTT→MT] Quick tap — keeping mic open for click-to-toggle mode.');
        return;
      }

      setIsRecording(false);
      setTimeLeft(Math.min(resetTimeout, PAGE_TIMEOUT));

      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;


      try {
        // [FIX recorder-desync + double-commit] Capture the live-recording state up front. A
        // duplicate onPointerUp (or a stop after the recorder never started) must NOT pause
        // again ("Already paused: please call .record() first") AND must NOT fall through to
        // commit: committing with no captured audio throws input_audio_buffer_commit_empty and
        // fires a spurious, often wrong-language, response (observed in test_A_2 — a second
        // empty commit produced "Entendido, voy a continuar en español").
        const wasRecording = wavRecorder.recording;
        if (wasRecording) {
          await wavRecorder.pause();
        } else {
          logRelay('[IndexPage] stopRecording: recorder not active, skipping pause.');
        }
        if (force) {
          mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        }

        if (!wasRecording) {
          logRelay('[IndexPage] stopRecording: no active recording — skipping commit (double-fire guard).');
          return;
        }

        // [FIX dead-socket-commit] If the relay socket is not OPEN, the audio frames we
        // streamed during recording were dropped (appendInputAudio is gated on isConnected),
        // so committing here would commit an empty buffer and throw "WebSocket is not
        // connected". Reconnect for the NEXT turn instead of losing the click silently, and
        // skip the commit for this (already-lost) utterance.
        const _ws = client?.realtime?.ws;
        console.log(
          `[stopRecording] commit attempt at ${new Date().toISOString()} | ` +
          `serial=#${(client as any)?._serial} isConnected=${client.isConnected()} wsState=${_ws?.readyState}`
        );
        if (!client.isConnected() || _ws?.readyState !== WebSocket.OPEN) {
          if (!sessionExpiredRef.current) {
            console.warn('[stopRecording] socket not OPEN — utterance lost; reconnecting for next turn.');
            try {
              await client.connect();
              logRelay('[stopRecording] reconnected — next turn will work.');
            } catch (err) {
              if ((err as Error)?.message !== 'Already connected') {
                console.error('[stopRecording] reconnect failed:', err);
              }
            }
          }
          return;
        }

        // ✅ Commit audio buffer then create response (GA API requires explicit commit)
        client.commitInputAudio();          // 📤 Commit the audio buffer
        client.createResponse();            // 🔄 Trigger assistant response
        logRelay('[IndexPage] Recording stopped. Audio committed and response requested.');


      } catch (error) {
        console.error('[IndexPage] Error stopping recording:', error);
      }
    };

  /**
   * Connect to Realtime API and initialize conversation
   */
  useEffect(() => {// Connect Realtime API 
     isRecordingRef.current = isRecording;
     isSpeakingRef.current = false;  // 🗣️ Tracks assistant speech playback
  }, [isRecording]);

  // Keep the latest startRecording reachable from effects and handlers that outlive a render.
  startRecordingRef.current = startRecording;

  // Page visibility, for hands-free: the mic is released when the page is hidden (the
  // visibilitychange handler above) and resumes by itself when the page is visible again.
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Hands-free has no mic button: keep the mic live while connected, unless the page is hidden or the
  // browser blocked the microphone. The 60 second recording timeout below never applies here.
  useEffect(() => {
    if (micMode !== 'hf' || !isConnected || micBlocked || isRecording || !pageVisible) return;
    startRecordingRef.current?.({ interrupt: false });
  }, [micMode, isConnected, micBlocked, isRecording, pageVisible]);

  // Leaving the blocked state by itself when the browser permission is granted.
  useEffect(() => {
    if (!micBlocked || !navigator.permissions?.query) return;
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (status?.state === 'granted') setMicBlocked(false);
    };
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((result) => {
        status = result;
        result.addEventListener('change', onChange);
      })
      .catch(() => {
        // Permission query is not supported in this browser; a tap on the mic asks again.
      });
    return () => status?.removeEventListener('change', onChange);
  }, [micBlocked]);

  // Switching mode: release the old capture first so exactly one capture path is ever active.
  // The relay is then told which VAD tier applies (hands-free is more sensitive, for barge-in).
  const prevMicModeRef = useRef(micMode);
  useEffect(() => {
    if (prevMicModeRef.current === micMode) return;
    prevMicModeRef.current = micMode;
    (async () => {
      if (isRecordingRef.current) {
        try {
          await wavRecorderRef.current.pause();
        } catch (err) {
          console.warn('[MicMode] pause on mode switch failed:', err);
        }
        setIsRecording(false);
      }
      inputLevelRef.current = 0;
      const client = clientRef.current;
      if (client?.isConnected?.()) {
        // Tell the relay which VAD tier to use (it reads the `handsfree` flag).
        client.updateSession({ turn_detection: getTurnDetection(micMode) } as ExtendedSessionResourceType);
      }
    })();
  }, [micMode]);

  // The bar shows "Assistant speaking" from isSpeakingRef, which is a ref, so mirror it into state.
  useEffect(() => {
    const id = setInterval(() => {
      setAssistantSpeaking((prev) => (prev === isSpeakingRef.current ? prev : isSpeakingRef.current));
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Hands-free has no mic button. When the browser blocked the microphone, the status line offers
  // "Try again", which asks for the microphone once more.
  const retryMic = async () => {
    await startRecordingRef.current?.({ interrupt: false });
  };

  // [PTT] Step 18: Safety timeout — auto-stop recording after 60s in PTT mode
  useEffect(() => {
    if (!isRecording || micModeRef.current !== 'ptt') return;
    const pttTimeout = setTimeout(() => {
      if (isRecordingRef.current) {
        logRelay('[PTT] 60s timeout — auto-stopping recording');
        stopRecording();
      }
    }, 60000);
    return () => clearTimeout(pttTimeout);
  }, [isRecording]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      const isCmdM = e.key?.toLowerCase() === 'm' && e.metaKey;
      if (!isCmdM) return;
      e.preventDefault();
      e.stopPropagation();
      (async () => {
        const client = clientRef.current;
        if (!client) return;
        try {
          client.cancelResponse?.('');
          client.sendUserMessageContent?.([{ type: 'input_text', text: 'stop' }]);
          logRelay('[IndexPageGuest] STOP sent via Cmd+M.');
        } catch (err) {
          console.warn('[IndexPageGuest] STOP via Cmd+M failed:', (err as any)?.message || err);
        }
      })();
    };

    window.addEventListener('keydown', handleKeydown, true);
    document.addEventListener('keydown', handleKeydown, true);
    return () => {
      window.removeEventListener('keydown', handleKeydown, true);
      document.removeEventListener('keydown', handleKeydown, true);
    };
  }, []);


  // ── Language Surface Helpers ────────────────────────────────────────────

  const originalBotSettingsRef = useRef<any>(null);

  const buildCurrentSurface = useCallback((): unknown => {
    // Always use the original English settings to prevent double-translation.
    // originalBotSettingsRef.current is set before buildCurrentSurface is called from renderSurface.
    const bs = originalBotSettingsRef.current ?? (() => {
      try { return JSON.parse(localStorage.getItem('botSettings') || 'null'); } catch { return null; }
    })();
    const mediaKeys: Record<string, string> = {};
    const rawMedia = (selectedBot as any)?.media ?? {};
    for (const [key, tag] of Object.entries(rawMedia)) {
      if ((tag as any)?.type !== 'command') {
        mediaKeys[key] = key;
      }
    }
    const commandTexts = new Set(
      Object.entries(rawMedia)
        .filter(([, v]) => (v as any)?.type === 'command')
        .flatMap(([key, v]) => [key.toLowerCase(), ((v as any).alias || '').toLowerCase()])
        .filter(Boolean)
    );
    const rawPresets = (selectedBot as any)?.presets ?? {};
    const presetsForTranslation = Object.fromEntries(
      Object.entries(rawPresets).filter(([, v]) => {
        const firstLine = String(v).split('\n')[0].trim().toLowerCase();
        return !commandTexts.has(firstLine);
      })
    );
    return {
      presets:   presetsForTranslation,
      welcome:   (selectedBot as any)?.welcome ?? '',
      title:     (selectedBot as any)?.title   ?? '',
      subtitle:  bs?.subtitle ?? '',
      footer:    bs?.footer   ?? null,
      flyout:    bs?.flyout   ?? null,
      mediaKeys,
    };
  }, []);

  const syncTranslatedBotSettings = useCallback((translated: any, originalBs: any): void => {
    if (!translated || !originalBs) return;
    try {
      const updated = {
        ...originalBs,
        ...(translated.subtitle !== undefined ? { subtitle: translated.subtitle } : {}),
        ...(translated.footer   !== undefined ? { footer:   translated.footer   } : {}),
        ...(translated.flyout   !== undefined ? { flyout:   translated.flyout   } : {}),
      };
      localStorage.setItem('botSettings', JSON.stringify(updated));
      document.cookie = `botSettings=${encodeURIComponent(JSON.stringify(updated))}; path=/`;
      window.dispatchEvent(new CustomEvent('botSettingsUpdated', { detail: updated }));
    } catch (err) {
      console.warn('[IndexPageGuest] syncTranslatedBotSettings failed:', err);
    }
  }, []);

  const renderSurface = useCallback(async (language: string): Promise<unknown> => {
    logRelay(`[renderSurface] Starting for language: ${language}`);
    const botId = (selectedBot as any)?.botid ?? 'unknown';

    // [SurfaceCache Fix 1] Anchor the pristine ENGLISH settings ACROSS RELOADS.
    // syncTranslatedBotSettings writes TRANSLATED subtitle/footer/flyout back into
    // localStorage.botSettings, so after a reload that key is polluted and the in-memory
    // ref is empty. The pristine copy persisted under botSettingsOriginal is preferred;
    // without it the source hash changed on every reload and the cache could never hit
    // (every reload paid a full LLM translation round-trip).
    const rawBotSettings = (() => {
      try { return JSON.parse(localStorage.getItem('botSettings') || 'null'); } catch { return null; }
    })();
    if (!originalBotSettingsRef.current) {
      const persistedOriginal = (() => {
        try { return JSON.parse(localStorage.getItem('botSettingsOriginal') || 'null'); } catch { return null; }
      })();
      originalBotSettingsRef.current = persistedOriginal ?? rawBotSettings;
      if (!persistedOriginal && rawBotSettings) {
        try { localStorage.setItem('botSettingsOriginal', JSON.stringify(rawBotSettings)); } catch { /* ignore */ }
      }
    }
    const englishBotSettings = originalBotSettingsRef.current ?? rawBotSettings;

    const surface = buildCurrentSurface();
    logRelay(`[renderSurface] Built surface keys:`, Object.keys(surface as object));

    // [SurfaceCache Fix 2] Hash ONLY the translatable English surface. Hashing the whole
    // selectedBot + settings objects churned the key on unrelated config changes and on
    // translated-settings pollution.
    const sourceHash = await hashSurface(surface);
    const cacheKey = buildSurfaceCacheKey({ botId, language, sourceHash });
    logRelay(`[renderSurface] cacheKey: ${cacheKey}`);

    // [SurfaceCache Fix 3] Optimistic render: apply the last-known translated surface for
    // this bot+language immediately (zero LLM wait), then continue to validate/refresh.
    const lastKeyName = `surface_lastkey_${botId}_${language}`;
    const lastKey = (() => { try { return localStorage.getItem(lastKeyName); } catch { return null; } })();
    if (lastKey && lastKey !== cacheKey) {
      try {
        const lastSurface = await getRenderedSurfaceWithFallback(lastKey);
        if (lastSurface) {
          const optimistic = validateRenderedSurface(surface, lastSurface);
          setTranslatedSurface(optimistic);
          syncTranslatedBotSettings(optimistic as any, englishBotSettings);
          logRelay(`[renderSurface] Optimistic last-known surface applied (key=${lastKey})`);
        }
      } catch { /* optimistic only — never block the real path */ }
    }

    const cached = await getRenderedSurfaceWithFallback(cacheKey);
    if (cached) {
      logRelay(`[renderSurface] Cache hit — validating cached surface`);
      const validated = validateRenderedSurface(surface, cached);

      /* ───────── TEMP DEBUG (remove me) — full translated surface from CACHE ───────── */
      console.log(`%c[TRANSLATED SURFACE — ${language} — from CACHE]`, 'color:#0FB5B5;font-weight:bold');
      console.log('FULL translated object:', validated);
      console.log('FULL translated object (JSON):\n' + JSON.stringify(validated, null, 2));
      console.log('mediaKeys present?', !!(validated as any)?.mediaKeys, '— mediaKeys:', (validated as any)?.mediaKeys);
      /* ──────────────────────────────────────────────────────────────────────────── */

      setTranslatedSurface(validated);
      syncTranslatedBotSettings(validated as any, englishBotSettings);
      try { localStorage.setItem(lastKeyName, cacheKey); } catch { /* ignore */ }
      return validated;
    }

    logRelay(`[renderSurface] Cache miss — calling translation API botId=${botId}`);
    const rendered = await renderSurfaceLanguage({ surface, language, botId });
    logRelay(`[renderSurface] API returned surface type:`, typeof rendered);

    const apiSucceeded = JSON.stringify(rendered) !== JSON.stringify(surface);
    logRelay(`[renderSurface] API translation produced change: ${apiSucceeded}`);

    const finalValidated = validateRenderedSurface(surface, rendered);
    logRelay(`[renderSurface] Setting translatedSurface. Presets sample:`,
      (finalValidated as any)?.presets?.['1'] ?? '(none)');

    /* ───────── TEMP DEBUG (remove me) — full translated surface from API ───────── */
    console.log(`%c[TRANSLATED SURFACE — ${language} — from API]`, 'color:#0FB5B5;font-weight:bold');
    console.log('Source surface SENT to translator:', surface);
    console.log('Raw RENDERED object from API:', rendered);
    console.log('FULL translated object (after validate):', finalValidated);
    console.log('FULL translated object (JSON):\n' + JSON.stringify(finalValidated, null, 2));
    console.log('mediaKeys present?', !!(finalValidated as any)?.mediaKeys, '— mediaKeys:', (finalValidated as any)?.mediaKeys);
    /* ───────────────────────────────────────────────────────────────────────── */

    setTranslatedSurface(finalValidated);
    syncTranslatedBotSettings(finalValidated as any, englishBotSettings);

    if (apiSucceeded) {
      await saveRenderedSurfaceWithFallback(cacheKey, {
        botId,
        language,
        sourceHash,
        surface: finalValidated,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      try { localStorage.setItem(lastKeyName, cacheKey); } catch { /* ignore */ }
    } else {
      logRelay(`[renderSurface] Skipping cache write — API returned original surface (translation failed)`);
    }
    return finalValidated;
  }, [buildCurrentSurface, syncTranslatedBotSettings]);

  const connectConversationRef = useRef<(() => Promise<void>) | null>(null);

  const applyLanguageMode = useCallback(async (language: string): Promise<void> => {
    logRelay(`[applyLanguageMode] language=${language}`);

    if (language === 'English') {
      // ── Revert to English ──────────────────────────────────────────────
      clearLanguageMode((selectedBot as any)?.botid); // removes per-bot localStorage key + clears cookie
      setTranslatedSurface(null);

      if (originalBotSettingsRef.current) {
        try {
          const orig = originalBotSettingsRef.current;
          localStorage.setItem('botSettings', JSON.stringify(orig));
          localStorage.setItem('botSettingsOriginal', JSON.stringify(orig));
          document.cookie = `botSettings=${encodeURIComponent(JSON.stringify(orig))}; path=/`;
          window.dispatchEvent(new CustomEvent('botSettingsReverted', { detail: orig }));
        } catch (err) {
          console.warn('[IndexPageGuest] Failed to restore botSettings on English revert:', err);
        }
        originalBotSettingsRef.current = null;
      }

      try {
        const client = clientRef.current;
        if (client?.isConnected?.()) {
          // REVERT_TO_ENGLISH marker tells the relay to clear its language lock.
          // Without this, the relay's HARD LOCK re-applies the Spanish suffix even when
          // the client sends English-only instructions.
          client.updateSession({ instructions: instructions + '\n\nREVERT_TO_ENGLISH: true' } as any);
          logRelay('[applyLanguageMode] session.update sent — reverted to English instructions');
        }
      } catch (err: any) {
        console.warn('[IndexPageGuest] session.update (English revert) failed:', err?.message);
      }
    } else {
      // ── Switch to target language ──────────────────────────────────────
      setLanguageMode((selectedBot as any)?.botid, language); // writes per-bot localStorage + relay cookie

      // Send the language session.update FIRST — synchronously, BEFORE the slow
      // renderSurface() await. The text-input handler sends the typed command
      // ("spanish mode") to the relay immediately after invoking this function. If the
      // session.update went out only AFTER renderSurface (an OpenAI translation round-
      // trip, ~1-2s), the model would generate its reply to that command using the
      // still-English instructions (race → English reply). Sending it up front means the
      // relay has the language rule applied before that reply is generated.
      try {
        const client = clientRef.current;
        if (client?.isConnected?.()) {
          const engMediaKeys = Object.keys((selectedBot as any)?.media ?? {})
            .filter(k => (selectedBot as any)?.media?.[k]?.type !== 'command');
          const mediaInstruction = engMediaKeys.length > 0
            ? `\n\nWhen your response includes references to media resources, output ONLY these EXACT English tokens verbatim, on their own line, even when responding in ${language} — do NOT translate them: ` +
              engMediaKeys.map(k => `"${k}"`).join(', ')
            : '';
          const langPreamble =
            `[CRITICAL LANGUAGE INSTRUCTION — READ AND APPLY THIS BEFORE ALL INSTRUCTIONS BELOW]\n` +
            `ACTIVE LANGUAGE: ${language}.\n` +
            `Every prose sentence you output MUST be in ${language} — including the Start Message, greetings, topic summaries, section menus, follow-up explanations, and all other conversational text.\n` +
            `The instructions below define WHAT to say and WHEN. You must say it in ${language}. Translate every prose sentence to ${language} before outputting it. Never output an English prose sentence when this instruction is active.\n` +
            `Exception: media button tokens / UI control tokens defined in the instructions below must stay in their exact English form. Everything else is prose and must be in ${language}.\n` +
            `[END CRITICAL LANGUAGE INSTRUCTION]\n\n`;
          const langInstructions =
            langPreamble +
            `${instructions}\n\nIMPORTANT LANGUAGE MODE: Respond exclusively in ${language}.\n` +
            `All spoken and text responses must be in ${language} only.\n` +
            `Follow all base SCB instructions verbatim — translate only the output language of your prose, not the intent, sequence, topic structure, or media tag behaviour. Every SCB-defined response must appear in ${language}, not in English.\n` +
            `Do not announce the language switch unless the user asks.` +
            mediaInstruction;
          client.updateSession({ instructions: langInstructions } as any);
          logRelay(`[applyLanguageMode] session.update sent (pre-renderSurface) — language=${language}, engMediaKeys=${engMediaKeys.length}`);
        }
      } catch (err: any) {
        console.warn('[IndexPageGuest] session.update (language switch) failed:', err?.message);
      }

      // Translate the UI surface (presets, labels, footer). Independent of the agent's
      // language and can finish after the session.update above.
      await renderSurface(language); // translate UI surface
    }
  }, [renderSurface]);

  const restoreLanguageMode = useCallback(async (): Promise<void> => {
    const stored = getLanguageMode((selectedBot as any)?.botid);
    if (!stored || stored === 'English') return;
    // applyLanguageMode restores UI surface AND sends session.update to reinforce
    // the language rule in the live relay session.
    await applyLanguageMode(stored);
  }, [applyLanguageMode]);

  // Fire translation as soon as the bot is resolved — don't wait for connectConversation
  useEffect(() => {
    if (!iBots?.length) return;
    const stored = getLanguageMode((selectedBot as any)?.botid);
    if (!stored || stored === 'English') {
      // English (untranslated) load: localStorage.botSettings is pristine — refresh the
      // persisted original so future translations and cache keys track current content
      // after settings deploys.
      try {
        const bs = localStorage.getItem('botSettings');
        if (bs) localStorage.setItem('botSettingsOriginal', bs);
      } catch { /* ignore */ }
      return;
    }
    renderSurface(stored).catch((err: any) =>
      console.warn('[IndexPageGuest] restoreLanguageMode (early) failed:', err?.message),
    );
  }, [iBots]);

  // ── Connect Conversation
  const connectConversation = useCallback(async () => {
    // [DIAG double-client] Entry marker with timestamp + the serial of the client we're about
    // to (re)connect, so the log shows exactly when/why a (re)connection happened and which
    // instance owns the resulting socket.
    console.log(
      `[connectConversation] ENTRY at ${new Date().toISOString()} | ` +
      `serial=#${(clientRef.current as any)?._serial} alreadyConnected=${clientRef.current?.isConnected?.()}`
    );
    if (sessionExpiredRef.current || isSessionExpiredLocked()) {
      sessionExpiredRef.current = true;
      showSessionExpiredUI();
      return;
    }
    if (!selectedBot || !selectedBot.endpoint) {
      console.warn('[IndexPage] connectConversation skipped: selectedBot not ready');
      return;
    }

    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    try {
      setIsConnected(true);
      setRealtimeEvents([]);
      suppressedAssistantIdsRef.current = new Set();

      // [FIX guest-connect] Don't block WebSocket on mic or audio permission.
      // On iOS Safari, AudioContext.resume() hangs until a user gesture occurs.
      // By making both mic and audio player non-blocking, text chat works immediately
      // on all platforms. Audio playback will be ready by the time the bot responds.
      if (!wavRecorder.stream && !wavRecorder.recording) {
        wavRecorder.begin().then(() => {
          console.log('[connectConversation] Mic permission granted');
        }).catch((err: any) => {
          console.warn('[connectConversation] Mic unavailable (text chat still works):', err?.message);
        });
      }

      wavStreamPlayer.connect().then(() => {
        wavStreamPlayer.setOutputVolume?.(1);
        console.log('[connectConversation] Audio player connected');
      }).catch((err: any) => {
        console.warn('[connectConversation] Audio player failed:', err?.message);
      });

      if (sessionExpiredRef.current) {
        return;
      }

      await client.connect();
      restoreLanguageMode().catch((err: any) =>
        console.warn('[IndexPageGuest] restoreLanguageMode failed:', err?.message),
      );

      // ⏱️ Heartbeat: send a lightweight client heartbeat to relay (no chat content)
      const start = Date.now();
      const maxAttempts = MAX_CONNECTION_ATTEMPTS ?? 14;
      const pingInterval = setInterval(async () => {
        const ws = client?.realtime?.ws;
        if (!client || !client.isConnected() || !ws || ws.readyState !== WebSocket.OPEN) {
          // [DIAG idle-timing] serial + sinceLastSend tell us whether this is the live client
          // and how long the socket has been idle when the keepalive can't fire.
          const _sinceSend = client?.realtime?.lastSendAt
            ? `${((Date.now() - client.realtime.lastSendAt) / 1000).toFixed(1)}s`
            : 'n/a';
          console.warn(`[IndexPage][Ping] ⚠️ Socket not ready, skipping heartbeat tick. at ${new Date().toISOString()}`, {
            serial: (client as any)?._serial,
            refSerial: (clientRef.current as any)?._serial,
            sinceLastSend: _sinceSend,
            hasClient: !!client,
            connected: client?.isConnected?.(),
            hasWs: !!ws,
            wsState: ws?.readyState,
          });
          return;
        }

        if (window.__ping.connectionAttempts >= maxAttempts) {
          clearInterval(pingInterval);
          logRelay(`[IndexPage][Ping] 🎙️ ConnectionAtempts: ${window.__ping.connectionAttempts}`);
          window.__ping.connectionAttempts = 1;
          window.__ping.pingTimestamp = Date.now();
          document.cookie = `pingTimestamp=${window.__ping.pingTimestamp}; path=/`;
          sessionExpiredRef.current = true;
          await finalizeMetricsRef.current?.('timeout');
          if (isRecordingRef.current) {
            await stopRecording(true);
          }
          mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
          try {
            await wavRecorderRef.current?.quit();
          } catch {
            // already ended
          }
          if (isSpeakingRef.current && client?.realtime?.ws?.readyState === WebSocket.OPEN) {
            try {
              client.realtime.send('response.cancel', {});
            } catch (err) {
              console.warn('[IndexPage] Failed to cancel response on timeout:', err);
            }
          }
          (window as any).clearSayInFlight?.();
          if ((client as any)._pingInterval) {
            clearInterval((client as any)._pingInterval);
            delete (client as any)._pingInterval;
          }
          client.disconnect();
          showSessionExpiredUI();
          return;
        }

        try {
          const sent = sendHeartbeat();
          if (!sent) {
            // [DIAG idle-timing] If the guard above passed (client.isConnected() true) but the
            // send still failed, the live socket flipped to non-OPEN between the two reads —
            // or clientRef was swapped (serial != refSerial). Both are double-client fingerprints.
            const _liveWs = clientRef.current?.realtime?.ws;
            console.warn(
              `[Ping] ❌ Failed to send relay heartbeat. at ${new Date().toISOString()} | ` +
              `closureSerial=#${(client as any)?._serial} refSerial=#${(clientRef.current as any)?._serial} ` +
              `refWsState=${_liveWs?.readyState}`
            );
            return;
          }

          window.__ping.pingTimestamp = Date.now();
          document.cookie = `pingTimestamp=${window.__ping.pingTimestamp}; path=/`;

          const elapsedSeconds = Math.floor((Date.now() - start) / 1000);
          const now = new Date().toLocaleTimeString();
          logRelay(
            `[IndexPage][Ping] ⏱️ Tick... Elapsed: ${elapsedSeconds}s at ${now} | Attempt: ${window.__ping.connectionAttempts}/${maxAttempts}`
          );

          window.__ping.connectionAttempts += 1;
        } catch (err) {
          console.warn('[Ping] ❌ Failed to update heartbeat:', err);
        }
      }, HEARTBEAT_PING);


      // ✅ Store ping interval on the clientRef so we can clear it on disconnect - DO NOT REMOVE
      (client as any)._pingInterval = pingInterval;    

      // ✅ Hook into sendUserMessageContent for timer reset
      client.sendUserMessageContent = ((originalSend) => (...args) => {
        // Any real user message counts as activity; heartbeat is updated locally.
        // Client no longer sends any synthetic 'ping' chat content.
        window.resetPingTimer();
        return originalSend.apply(client, args);
      })(client.sendUserMessageContent);


      // ! FIRST CONNECTION ATTEMPT HERE
      client.realtime.on('close', async (event: CloseEvent) => {
      console.warn('🛑 [WebSocket Closed] Relay connection was closed');
        console.warn('↪️ Code:', event.code, '| Reason:', event.reason);
        const isConnected = client.isConnected();
        const wsState = client?.realtime?.ws ? client.realtime.ws.readyState : 'unavailable';
        console.warn('📡 client.isConnected():', isConnected);
        console.warn('📶 WebSocket readyState:', wsState);

        try {
         
          client.isConnected();
  
        } catch (err: any) {
          const message =  err.message + ' \n\nConnection to server Failed\nPlease check your internet connections and reload.';
          alert(message);
          return;
        }
      });

      const safeVoice = allowedVoices.includes(selectedVoice as any) ? selectedVoice as typeof allowedVoices[number] : undefined;
      // [S2-B §3.2] the record's host id — no Nova substitution for non-asst_ ids.
      const sessionAssistantId = selectedHostId || undefined;

      const storedLang = getLanguageMode((selectedBot as any)?.botid);
      const sessionInstructions = (() => {
        if (!storedLang || storedLang === 'English') return instructions;
        const engMediaKeys = Object.keys((selectedBot as any)?.media ?? {})
          .filter(k => (selectedBot as any)?.media?.[k]?.type !== 'command');
        const mediaTokenLine = engMediaKeys.length > 0
          ? `\n\nWhen your response includes references to media resources, output ONLY these EXACT English tokens verbatim, on their own line, even when responding in ${storedLang} — do NOT translate them: ` +
            engMediaKeys.map(k => `"${k}"`).join(', ')
          : '';
        return `${instructions}\n\nIMPORTANT LANGUAGE MODE: Respond exclusively in ${storedLang}.\n` +
          `All spoken and text responses must be in ${storedLang} only.\n` +
          `Do not announce the language switch unless the user asks.` +
          mediaTokenLine;
      })();

      logRelay('[Init] updateSession payload:', {
        instructions: sessionInstructions,
        input_audio_transcription: { model: 'whisper-1' },
        assistant_id: sessionAssistantId,
        voice: safeVoice,
      });
      logRelay(`[${micModeRef.current === 'ptt' ? 'PTT' : 'HF'}] turn_detection: ${micModeRef.current === 'ptt' ? 'server_vad (create_response:false)' : 'server_vad (defaults)'}`);
      client.updateSession({ // Web Socket connect and send data
        instructions: sessionInstructions,
        input_audio_transcription: { model: 'whisper-1' },
        // Hands-free: platform defaults. Push-to-Talk: custom VAD with create_response:false so the
        // client controls send timing. One definition for both: src/lib/micMode.ts.
        turn_detection: getTurnDetection(micModeRef.current),
        assistant_id: sessionAssistantId, // Default assistant_id
         voice: safeVoice
      } as ExtendedSessionResourceType);

      // [DIAG voice/lang] Record exactly which voice + language this session was opened with,
      // so a mid-session voice/gender or language flip can be traced to a reconnect that
      // re-resolved them differently.
      console.log(
        `[connectConversation] session.update sent | serial=#${(client as any)?._serial} ` +
        `voice=${safeVoice ?? '(default/alloy)'} language=${storedLang || 'English'} at ${new Date().toISOString()}`
      );

      console.log(`[DIAG-CLIENT] Sending "Start Session" | t=${Date.now()}`);
      client.sendUserMessageContent([
        { type: 'input_text', text: `Start Session` },

      ]);

      /* Begin conversation updated  - MAIN LOOP */

          client.on('conversation.updated', async ({ item, delta }: any) => {
            const conversationItems = client.conversation.getItems() as any[];
            speechStatus = item.status
            
   
            logRelay(speechStatus);

            // IndexPageGuest.tsx — conversation.updated
            // [API] item.status is a string enum ("completed"|"in_progress"|"incomplete")
            if (item?.status === 'in_progress' && item?.role === 'assistant') {
              // New assistant reply started; allow audio to play
              stopSpeechRef.current = false;
              isSpeakingRef.current = true;
            } else if (item?.status === 'completed') {
              speechStatus = 'completed';
              isSpeakingRef.current = false;
            }
            
            if (delta?.threadCreated) {
              logRelay("✅ - [conversation.updated] Received new thread from relay.js:", delta.threadCreated);

            setItems((prevItems) => {
              const withoutLoading = prevItems.filter(
                (i) => i.formatted?.text !== "LOADING ASSISTANT..."
              );
              return [
                ...withoutLoading,
                ...conversationItems
              ];
            });
            }

            if (DEBUG_CONVERSATION) {
              logRelay(`[IndexPage Debug] Using global assistantId: ${assistantId}`);
              logRelay(`[IndexPage Debug] Using global selectedBot:`, selectedBot);
            }
            if (!selectedBot) {
              console.error('[IndexPage Debug] selectedBot is not initialized. Skipping updates.');
              return;
            }
          
            const isRealtimeMode = selectedBot.endpoint.includes('realtime');
            if (DEBUG_CONVERSATION) {
              logRelay(`[IndexPage Debug] isRealtimeMode: ${isRealtimeMode}`);
            }
            const micButton = document.getElementById('toggle-voice') as HTMLButtonElement;
            const stopVoiceButton = document.getElementById('stop-voice') as HTMLButtonElement;
          
            if (micButton) {
              micButton.disabled = !isRealtimeMode;
              micButton.style.display = isRealtimeMode ? 'inline-block' : 'none';
              if (DEBUG_CONVERSATION) {
                logRelay(`[IndexPage Debug] Mic button - disabled: ${micButton.disabled}, display: ${micButton.style.display}`);
              }
            }
          
            const visualizationDiv = document.getElementById('visualization') as HTMLDivElement;
                    
            if (visualizationDiv) {
                                                                                                                                                         visualizationDiv.style.display = isRealtimeMode ? 'inline-block' : 'none'; 
              logRelay(`[IndexPage Debug] Stop button - display: ${visualizationDiv.style.display}`);
            }
            
            if (stopVoiceButton) {
              stopVoiceButton.disabled = !isRealtimeMode;
              stopVoiceButton.style.display = isRealtimeMode ? 'inline-block' : 'none';
            }

            // Handle audio updates (only in realtime mode)
            if (isRealtimeMode && delta?.audio && !stopSpeechRef.current && !isMutedRef.current) {
              isSpeakingRef.current = true;
              if (typeof window.resetPingTimer === 'function') {
                window.resetPingTimer();
              }

              // Ensure the audio context is running before sending PCM
              try {
              if (!wavStreamPlayer.context) {
                await wavStreamPlayer.connect();
              } else if (wavStreamPlayer.context.state === 'suspended') {
                await wavStreamPlayer.context.resume();
              }
              wavStreamPlayer.setOutputVolume?.(1);
              } catch (err) {
                console.warn('[IndexPage] Failed to restore audio context:', err);
              }

              // GA API sends audio as base64 - decode to Int16Array for wav player
              const audioData = typeof delta.audio === 'string'
                ? base64ToInt16Array(delta.audio)
                : delta.audio;
              wavStreamPlayer.add16BitPCM(audioData, item.id);
            }

            logRelay(`<< item.status ${speechStatus}`)

            // Track function call state for thinking indicator
            if (item.type === 'function_call' && item.status === 'in_progress') {
              functionCallPendingRef.current = true;
              setStatusThinking(true);
            }

            // Handle transcript updates
            if (item.formatted?.transcript && item.role === 'assistant') {
              functionCallPendingRef.current = false;
              setStatusThinking(false);
              if (typeof window.resetPingTimer === 'function') {
                window.resetPingTimer();
              }
              // Note: Don't add duplicate items here - conversationItems already has them
              // and normalized items will be set at the end
            }
            
            // ...existing code for standby command...

          // Drop empty assistant messages (no text/transcript)
          // [FIX ] Also skip assistant messages during function call to prevent fallback display
          const cleanedItems = conversationItems.filter((ci: any) => {
            if (ci?.id && suppressedAssistantIdsRef.current.has(ci.id)) {
              return false;
            }
          
            // Suppress SAY injected user instruction (contains marker text)
            if (ci?.role === 'user') {
              const ciText = ci?.formatted?.text || ci?.content?.[0]?.text || '';
              if (ciText.includes('READ THE FOLLOWING TEXT EXACTLY AS WRITTEN')) {
                suppressedAssistantIdsRef.current.add(ci.id);
                return false;
              }
            }

            // Always filter empty assistant messages
            if (
              ci?.role === 'assistant' &&
              (ci?.formatted?.text ?? '').trim() === '' &&
              (ci?.formatted?.transcript ?? '').trim() === ''
            ) {
              return false;
            }
            // Do NOT drop previously completed assistant messages while tools run; only drop known suppressed ids
            return true;
          });   


            // Normalize items to ensure formatted.text is populated from content array
            // Server items have content: [{type: 'input_text', text: '...'}] but ChatUI expects formatted.text
            const normalizedItems = cleanedItems.map((convItem: any) => {
              // Skip if already has formatted text
              if (convItem.formatted?.text) {
                return convItem;
              }
              // Use transcript if available (from audio transcription)
              if (convItem.formatted?.transcript) {
                return {
                  ...convItem,
                  formatted: { ...convItem.formatted, text: convItem.formatted.transcript }
                };
              }
              // Extract text from content array
              const textContent = convItem.content?.find((c: any) => c.type === 'input_text');
              const audioContent = convItem.content?.find((c: any) => c.type === 'audio');
              const extractedText = textContent?.text || audioContent?.transcript || '';
              if (extractedText) {
                return {
                  ...convItem,
                  formatted: { ...convItem.formatted, text: extractedText }
                };
              }
              return convItem;
            });

            // Filter out items with no displayable text (prevents empty "You:" lines)
            const displayableItems = normalizedItems.filter((convItem: any) => {
              const text = convItem.formatted?.text || convItem.formatted?.transcript || '';
              const hasText = text.trim().length > 0;
              // Debug: log filtered items
              if (!hasText) {
                logRelay(`[Normalization] Filtering out empty item: role=${convItem.role}, id=${convItem.id}, content=${JSON.stringify(convItem.content?.[0])?.slice(0, 80)}`);
              }
              return hasText;
            });

            // Debug: log final items being set
            logRelay(`[Normalization] Setting ${displayableItems.length} items (filtered from ${cleanedItems.length})`);
            /*displayableItems.forEach((di: any, idx: number) => {
              logRelay(`  [${idx}] role=${di.role}, text="${(di.formatted?.text || '').slice(0, 50)}..."`);
            });*/

          // Update the conversation items
            setItems(displayableItems);
          });
          
          client.on('cancelResponse', (id: string, sampleCount?: number) => {
            logRelay('[IndexPage] **** client.cancelResponse called: ', id, sampleCount);
          });

        
      /* end conversation updated */      
      
      /* THIS RECEIVES ALL REALTIME MESSAGE EVENTS */
      if (eventBoundRef.current) {
        eventBoundRef.current = false;
        client.on('realtime.event', async (realtimeEvent: RealtimeEvent) => {
        setRealtimeEvents((prevEvents) => [...prevEvents, realtimeEvent]);

        const evtType = realtimeEvent.event?.type;
        // Hands-free: the relay's server VAD ends each turn (it commits the audio buffer but never
        // creates a response: relayAPI.js strips the page's turn_detection and sets its own with
        // create_response:false), so the page requests the response once per committed turn.
        if (evtType === 'input_audio_buffer.committed' && micModeRef.current === 'hf') {
          clientRef.current?.createResponse();
        }
        // Hands-free barge-in: when the user starts talking over the assistant, flush the audio still queued
        // in the player and drop the rest of that response. (With the hands-free VAD tier the provider also
        // cancels the response itself.) Safe when nothing is playing.
        if (evtType === 'input_audio_buffer.speech_started' && micModeRef.current === 'hf') {
          stopSpeechRef.current = true;
          isSpeakingRef.current = false;
          void wavStreamPlayerRef.current.interrupt();
        }
        const callName =
          realtimeEvent.event?.name ||
          realtimeEvent.event?.function_call?.name ||
          realtimeEvent.event?.function_call?.type;

        // ✅ Show thinking indicator when file_search starts
        if (
          typeof evtType === 'string' &&
          evtType.startsWith('response.function_call') &&
          callName === 'file_search'
        ) {
          if (!functionCallPendingRef.current) {
            functionCallPendingRef.current = true;
            setStatusThinking(true);
          }
        }

        // ✅ Hide thinking indicator when file_search completes
        if (
          typeof evtType === 'string' &&
          (evtType === 'response.function_call_arguments.done' ||
            evtType === 'response.done') &&
          callName === 'file_search'
        ) {
          functionCallPendingRef.current = false;
          setStatusThinking(false);
        }

        /* TOKEN LIMIT RESPONSE  */
        // Ensures only token-limited responses trigger continue, reducing false positives and stalling. */
        if (
          realtimeEvent.event?.type === 'response.done' &&
          realtimeEvent.event?.response?.status === 'incomplete' &&
          realtimeEvent.event?.response?.status_details?.reason === 'max_output_tokens'
          
        ) {
          console.warn('[IndexPage] Max token limit hit — triggering continuation.');
            setTimeout(() => {
              stopSpeechRef.current = false;
              isSpeakingRef.current = false;
            //client.sendUserMessageContent([{ type: 'input_text', text: 'continue' }]);
            setItems((prevItems) => [// Spoof Assistant Speech
              ...prevItems,
              makeAssistantItem('Continue (Y/N)')
            ]);
            window.resetPingTimer?.();
          }, 300);
        } else if (
          realtimeEvent.event?.type === 'response.done'
        ) {
          // 🔹 Handle other types of response.done events
          logRelay('[IndexPage] Response done (non-token-limit case).');
          isSpeakingRef.current = false;// isAssistantSpeaking
          window.resetPingTimer?.();

          // [FIX mic-bug-02] Clean up the injected SAY instruction from conversation
          // history after the SAY response completes. This prevents "READ THE FOLLOWING
          // TEXT..." from leaking into future conversation context and confusing the model.
          if ((window as any).sayInjectedItemId && (window as any).saySuppressActive) {
            try {
              client.deleteItem((window as any).sayInjectedItemId);
              logRelay(`[IndexPage][SAY] Deleted injected SAY item: ${(window as any).sayInjectedItemId}`);
            } catch (e) {
              console.warn('[IndexPage][SAY] Failed to delete SAY item:', e);
            }
            (window as any).sayInjectedItemId = null;
          }
          (window as any).clearSayInFlight?.();
      }
        
        if (realtimeEvent.event?.event === 'connection-status') {
          if (realtimeEvent.event?.status === 'timeout') {
            logRelay('[Debug] ✅ Timeout received from relay.js');
            if (sessionExpiredRef.current) return;

            setItems((prevItems) => [
              ...prevItems,
              makeAssistantItem(
                realtimeEvent.event.reason || '⏱️ This session has timed out due to inactivity.'
              ),
            ]);

            if (isRecordingRef.current) {
              await stopRecording(true);
            }

            mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
            try {
              await wavRecorderRef.current?.quit();
            } catch {
              // already ended
            }

            if (isSpeakingRef.current && client?.realtime?.ws?.readyState === WebSocket.OPEN) {
              try {
                client.realtime.send('response.cancel', {});
              } catch (err) {
                console.warn('[IndexPage] Failed to cancel response on timeout:', err);
              }
            }

            (window as any).clearSayInFlight?.();

            await finalizeMetricsRef.current?.('timeout');
            client.disconnect();

            if ((client as any)._pingInterval) {
              clearInterval((client as any)._pingInterval);
              delete (client as any)._pingInterval;
            }

            sessionExpiredRef.current = true;
            showSessionExpiredUI();
          } else if (realtimeEvent.event?.status === 'loading') {
            // File search in progress; suppress assistant fallbacks until completion
            functionCallPendingRef.current = true;
            setStatusThinking(true);
          } else if (realtimeEvent.event?.message) {
            // Show loading / status messages like "Searching..."
            setItems((prevItems) => [
              ...prevItems,
              makeAssistantItem(realtimeEvent.event.message),
            ]);
          }
        }

        // ✅ [FIX Vector Search issue01] Handle clear event to discard fallback text before file_search results
          if (realtimeEvent.event?.event === 'clear') {
            logRelay('[IndexPage] Clear event received - ignoring to preserve session history');
            return; // don't process further for this event
          }

        // Extract the `threadCreated` event
        if (realtimeEvent.event?.event === "threadCreated") {
          // [FIX mic-bug-07] Only process threadCreated once per session — relay safety
          // resends were removed, but guard against any duplicate from reconnects etc.
          if (threadReceivedRef.current) {
            logRelay("[IndexPage] Duplicate threadCreated ignored");
            return;
          }
          threadReceivedRef.current = true;

          currentThreadId = realtimeEvent.event.runObject.thread_id;
          currentRunId = realtimeEvent.event.runObject.id;
          localStorage.setItem('currentThreadId', JSON.stringify(currentThreadId))
          localStorage.setItem('currentRunId', JSON.stringify(currentRunId))
         

          //Thinking...
          const thinkingLabels = [
            'Thinking',
            'Cogitating',
            'Pondering',
            'Ruminating',
            'Reasoning',
            'Perceiving',
            'Exploring'
          ];

          const randomThinkingLabel =
            thinkingLabels[Math.floor(Math.random() * thinkingLabels.length)];

          setItems((prevItems) => {
            const cleaned = prevItems.filter(
              (i) => i.formatted?.text !== 'LOADING ASSISTANT...'
            );

            const alreadyThinking = cleaned.some(
              (i) =>
                i.role === 'system' &&
                thinkingLabels.some((label) => i.formatted?.text === `${label}...`)
            );

            return alreadyThinking
              ? cleaned
              : [...cleaned, { role: 'system', formatted: { text: `${randomThinkingLabel}...` } }];
          });
          
          logRelay("[IndexPage] ✅ Received thread from relay.js:", currentThreadId);// Has to agree with relay.js EMIT thread
          
          // Enable Converstaion Div on Server Ready
          const convoDiv = document.getElementById("conversation");
          if (convoDiv) {
            convoDiv.style.pointerEvents = 'auto';
            convoDiv.style.opacity = '1';
          }

          // 🔹 Send client welcome message now that the thread is confirmed 
          let userName = user.firstname;

          if (userName === undefined ){
            userName = localStorage.getItem('name');
          }
          const safeName = isGuestPlaceholderName(userName) ? '' : (userName || '').trim();

          //Get Opening Greeting with query string Prompt if present
          let queryStringPrompt = searchParams.get('prompt');
          if (queryStringPrompt && queryStringPrompt !== '' && !promptSentRef.current) {
            logRelay('-> Opening Greeting Query String Prompt Detected (suppressed extra send): ', queryStringPrompt);
            // Do not send here; backend already receives prompt via querystring
            localStorage.setItem('pendingPrompt', ``);
            promptSentRef.current = true;
          } else {
            // [LanguageMode] Read the stored language for this bot using the URL id param
            // (selectedBot?.botid may be stale in this closure — URL param is always fresh).
            const _greetBotId = new URLSearchParams(window.location.search).get('id') || '';
            const _greetLang = getLanguageMode(_greetBotId);
            const _inNonEnglishMode = !!_greetLang && _greetLang !== 'English';

            if (_inNonEnglishMode) {
              // Do NOT send an English greeting — the session.update already contains
              // "Respond exclusively in ${language}" instructions, and an English first
              // message would cause the model to reply in English regardless.
              // A silent start lets the agent wait for the user's first input in the
              // target language, keeping the session fully in that language.
              console.log(`[DIAG-CLIENT] Suppressing English greeting — language mode is "${_greetLang}" | t=${Date.now()}`);
            } else {
              logRelay('-> Opening Greeting, no prompt: ');
              const greetingText = safeName
                ? `Hi, it's ${safeName} let's get started!`
                : `Hi, let's get started!`;
              console.log(`[DIAG-CLIENT] Sending greeting "${greetingText}" | t=${Date.now()}`);
              client.sendUserMessageContent([
                { type: 'input_text', text: greetingText },
              ]);
            }
          }

          // ✅ Store in DB 
          let userId = document.cookie
                  .split('; ')
                  .find(row => row.startsWith('userId='))
                  ?.split('=')[1]; // Retrieve token value
          if(!userId) {
            userId = localStorage.getItem('userId') || ''; // Fallback to localStorage
           logRelay('UserId:', userId); // Log token to confirm
          }
          const botId = assistantId || ''; // Get assistant ID
          logRelay('ConversationUserId:', userId); // Log token to confirm
          
          const token = document.cookie
                .split('; ')
                .find(row => row.startsWith('token='))
                ?.split('=')[1]; // Retrieve token value
                // logRelay('Token:', token); // Log token to confirm

          // Check guest login state from localStorage/sessionStorage or other flag
          // Patch Fix for slower connectors
          const waitForGuest = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

          
          (async () => {
            let guestToken = localStorage.getItem('token');
            let guestUserId = localStorage.getItem('userId');
            let guestUserName = localStorage.getItem('name');
          
            for (let i = 0; i < 24 && (!guestToken || !guestUserId); i++) {
              await waitForGuest(250);
              guestToken = localStorage.getItem('token');
              guestUserId = localStorage.getItem('userId');
              guestUserName = localStorage.getItem('name');
            }
          
            logRelay('GuestToken:', guestToken);
            logRelay('GuestUserId:', guestUserId);
            logRelay('GuestUserName:', guestUserName);
          
            if (!token && !userId && !guestToken && !guestUserId) {
              // ⛔ FIX 4 — the OTHER bounce site, bounded the same way. This is the 6s-poll path;
              // the confirmed 2026-08-14 loop went through the synchronous gate instead, but an
              // unbounded cycle here is the same defect waiting for a different trigger.
              const { attempts, exceeded } = bumpGuestSignInAttempts(sessionStorage);
              if (exceeded) {
                logRelay(`⛔ Guest sign-in produced no token after ${attempts} attempts — stopping`);
                setGuestLoopError(
                  `Guest sign-in did not produce a session after ${attempts} attempts. `
                  + 'This usually means the sign-in service returned an incomplete response.',
                );
                return;
              }
              alert("⏱️ I'm having trouble signing in, reload or click OK to try again.");
              purgeStaleAuthKeys({ localStorage, sessionStorage, document }); // [S2-A]
              window.location.href = getSignInUrl();
              return;
            }
            // A guest session really started — the bound resets so a later, unrelated
            // hiccup gets its own full budget rather than inheriting this one's count.
            resetGuestSignInAttempts(sessionStorage);
          })();
          //
          const meta = JSON.parse(localStorage.getItem("metadata") || '{}');
          const metadata = {
            ...meta,
            userId: userId || '',
            botId: botId || '',
            threadId: currentThreadId || '',
            runId: currentRunId || '',
          };
          logRelay('Metadata:', metadata); // Log metadata to confirm

          const tools = {
            type: "file_search",
            // vector_store_location: "vs_oK5PAVwEVWz0OnwkW2nztvMN",
          };
          logRelay('Tools:', tools); // Log metadata to confirm
       }       
      });
      }

      client.on('error', (error: any) => {
        console.error('[IndexPage] Error:', error);
      });

      client.on('disconnected', () => {
        logRelay('[IndexPage] Disconnected from Realtime API.');
        setIsConnected(false);
      });
    } catch (error) {
      console.error('[IndexPage] Connection failed:', error);
    }
  }, []);

 
  

  /**
   * Disconnect and clean up resources
   */
  const disconnectConversation = useCallback(async () => {
  if (selectedBot?.endpoint?.includes('realtime')) {  
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      setIsConnected(false);
      setRealtimeEvents([]);
      setItems([]);
      suppressedAssistantIdsRef.current = new Set();
      threadReceivedRef.current = false; // [FIX mic-bug-07] Reset so reconnect can process threadCreated

      // ✅ Clear client ping interval if it exists
      if ((client as any)._pingInterval) {
        clearInterval((client as any)._pingInterval);
        delete (client as any)._pingInterval;
        logRelay('[Ping] ⛔ Cleared heartbeat ping interval');
      }

      client.disconnect();
      try {
        if (wavRecorder && typeof wavRecorder.getStatus === 'function') {
          const status = wavRecorder.getStatus();
          if (status !== 'ended') {
            await wavRecorder.end();
          }
        } else if (wavRecorder?.recording) {
          await wavRecorder.end();
        }
      } catch (err) {
        logRelay('[IndexPage] wavRecorder end skipped or failed:', err);
      }

      try {
        if (wavStreamPlayer) {
          await wavStreamPlayer.interrupt();
        }
      } catch (err) {
        logRelay('[IndexPage] wavStreamPlayer interrupt failed:', err);
      }
    } else {
     logRelay('[IndexPage] Non-realtime endpoint, skipping wavRecorder initialization...');
  }

  }, []);

  /**
   * Cleanup and resource management - With re-fire when selectedBot becomes available.
   */
  useEffect(() => {// CleanUp Conversation
    if (sessionExpiredRef.current || isSessionExpiredLocked()) {
      sessionExpiredRef.current = true;
      setIsConnected(false);
      showSessionExpiredUI();
      return;
    }
    // [S2-A hotfix 2026-09-21] `botReady` is bumped by runInit right after it creates the
    // client with the real (metadata-bearing) URL. Without it this effect fired only
    // at mount, when clientRef still held the default-URL placeholder (:1071) — pre-S2-A that
    // was masked by runInit's prefix being fully synchronous. S2-B: every id takes this path
    // now; the failure state never bumps, so an unavailable bot never connects.
    if (!botReady) return;
    if (!selectedBot || !selectedBot.endpoint) return;
    connectConversation();
     return () => {
      disconnectConversation();
    };
  }, [disconnectConversation, botReady]);

  // Manually clear the session
  const clearSession = useCallback(async () => {
    if (!assistantId) return;

    await finalizeMetricsRef.current?.('user_stop');

    const sessionId = `session_${assistantId}`;
    localStorage.removeItem(sessionId);
    setItems([]); // Clear UI messages
    logRelay(`[clearSession] User cleared session: ${sessionId}`);
  }, [assistantId]);

  // Keep connectConversationRef current so applyLanguageMode can reconnect relay
  useEffect(() => {
    connectConversationRef.current = connectConversation;
  }, [connectConversation]);


  useEffect(() => {// Auto-scroll conversation div TO bottom when messages update
    const conversationDiv = document.getElementById("conversation");
    if (conversationDiv) {
      conversationDiv.scrollTop = conversationDiv.scrollHeight; // Scroll to the top instead of the bottom
    }
    if (statusThinkingVisibleRef.current) {
      setStatusThinking(true); // re-apply to newest item only
    }
  }, [items]);
 
  useEffect(() => {// Events Auto-scroll
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      eventsEl.scrollTop = eventsEl.scrollHeight;
    }
  }, [realtimeEvents]);

   /**
   * Set up render loops for the visualization canvas
   */
  //const clientCanvas = clientCanvasRef.current!;
  /* Canvas Render loop removed  */

  /* JSX COMPONENTS - Conditionally load CHATUI or ChatUI White, etc */

  // Merge translated text fields over selectedBot — only presets/welcome/title are overlaid
  const effectiveBot = (() => {
    // _currentLanguage is always computed so ChatUI has it immediately — even during the
    // cache-miss window when translatedSurface is still null. Without this, isNonEnglish
    // stays false while the translation API call is in flight and preset clicks route wrong.
    const _activeLang = getLanguageMode((selectedBot as any)?.botid || '');

    if (!translatedSurface) {
      return {
        ...(selectedBot as any),
        _currentLanguage: _activeLang || 'English',
      };
    }
    const ts = translatedSurface as any;
    const baseMedia = (selectedBot as any)?.media ?? {};
    const translatedMediaKeys = ts?.mediaKeys as Record<string, string> | undefined;
    const mergedMedia = { ...baseMedia };
    if (translatedMediaKeys) {
      for (const [engKey, transKey] of Object.entries(translatedMediaKeys)) {
        if (mergedMedia[engKey] && transKey !== engKey) {
          mergedMedia[transKey] = mergedMedia[engKey];
        }
      }
    }
    return {
      ...(selectedBot as any),
      ...ts,
      media: mergedMedia,
      _originalPresets: (selectedBot as any)?.presets ?? {},
      _currentLanguage: _activeLang || 'English',
    };
  })();

  // ⛔ FIX 4 — THE TERMINAL STATE. Without something rendered here the bound would merely stop
  // redirecting, leaving a blank page — which is what the in-flight latch already does and is
  // barely better than the loop. A bounded failure has to SAY what failed and what to do.
  if (guestLoopError) {
    return (
      <div id="root" data-component="IndexPageGuest-loop-stopped"
           style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', maxWidth: 640 }}>
        <h2 style={{ marginBottom: '0.5rem' }}>Sign-in stopped</h2>
        <p style={{ lineHeight: 1.5 }}>{guestLoopError}</p>
        <button
          style={{ marginTop: '1rem', padding: '8px 16px', cursor: 'pointer' }}
          onClick={() => {
            try {
              sessionStorage.clear();
              localStorage.removeItem('tokenTimestamp');
              document.cookie = 'token=; path=/; Max-Age=0';
              document.cookie = 'userId=; path=/; Max-Age=0';
            } catch { /* best effort */ }
            window.location.href = getSignInUrl();
          }}
        >
          Clear stored session and retry
        </button>
      </div>
    );
  }

  return (
    <div id="root" data-component="IndexPage">
    {assistantId === 'asst_RVOwZb5CDbjHbvIamdyMMRik'
    ? (
     <ChatUIMin
        Header={Header}
        Footer={Footer}
        iBots={iBots}
        assistantId={assistantId}
        isConnected={isConnected}
        timeLeft={timeLeft}
        connectConversation={connectConversation}
        setTimeLeft={setTimeLeft}
        sendTextQuery={sendTextQuery}
        isRecording={isRecording}
        startRecording={startRecording}
        stopRecording={stopRecording}
        clientCanvasRef={clientCanvasRef}
        serverCanvasRef={serverCanvasRef}
        items={items}
        setItems={setItems}
        clientRef={clientRef}
        iBookThumbnail={null}
        showEventsLog={showEventsLog}
        setShowEventsLog={setShowEventsLog}
        eventsScrollRef={eventsScrollRef}
        realtimeEvents={realtimeEvents}
        selectedBot={effectiveBot}
        clearSession={clearSession}
        toggleMute={toggleMute}
        isMuted={isMuted}
        onPromptClicked={handlePromptClicked}
        onUserQuestion={handleUserQuestion}
        onSessionCleared={clearSession}
        onLanguageCommand={applyLanguageMode}


    />
    ) : assistantId === 'asst_XPNDfJ387Uz8E4rcYq0E79Qy' //TJ
     ? (
      <ChatUIWhite
        Header={Header}
        Footer={Footer}
        iBots={iBots}
        assistantId={assistantId}
        isConnected={isConnected}
        timeLeft={timeLeft}
        connectConversation={connectConversation}
        setTimeLeft={setTimeLeft}
        sendTextQuery={sendTextQuery}
        isRecording={isRecording}
        startRecording={startRecording}
        stopRecording={stopRecording}
        clientCanvasRef={clientCanvasRef}
        serverCanvasRef={serverCanvasRef}
        items={items}
        setItems={setItems}
        clientRef={clientRef}
        iBookThumbnail={null}
        showEventsLog={showEventsLog}
        setShowEventsLog={setShowEventsLog}
        eventsScrollRef={eventsScrollRef}
        realtimeEvents={realtimeEvents}
        selectedBot={effectiveBot}
        clearSession={clearSession}
        toggleMute={toggleMute}
        isMuted={isMuted}
        onPromptClicked={handlePromptClicked}
        onUserQuestion={handleUserQuestion}
        onSessionCleared={clearSession}
        onLanguageCommand={applyLanguageMode}
      />
    ) : (
      <ChatUI
        Header={Header}
        Footer={Footer}
        iBots={iBots}
        assistantId={assistantId}
        isConnected={isConnected}
        timeLeft={timeLeft}
        connectConversation={connectConversation}
        setTimeLeft={setTimeLeft}
        sendTextQuery={sendTextQuery}
        isRecording={isRecording}
        startRecording={startRecording}
        stopRecording={stopRecording}
        clientCanvasRef={clientCanvasRef}
        serverCanvasRef={serverCanvasRef}
        items={items}
        setItems={setItems}
        clientRef={clientRef}
        iBookThumbnail={null}
        showEventsLog={showEventsLog}
        setShowEventsLog={setShowEventsLog}
        eventsScrollRef={eventsScrollRef}
        realtimeEvents={realtimeEvents}
        selectedBot={effectiveBot}
        clearSession={clearSession}
        toggleMute={toggleMute}
        isMuted={isMuted}
        micMode={micMode}
        micBlocked={micBlocked}
        assistantSpeaking={assistantSpeaking}
        inputLevelRef={inputLevelRef}
        onRetryMic={retryMic}
        interruptPlayback={interruptPlayback}
        stopButtonSendsStop={STOP_BUTTON_SENDS_STOP}
        onPromptClicked={handlePromptClicked}
        onUserQuestion={handleUserQuestion}
        onSessionCleared={clearSession}
        onLanguageCommand={applyLanguageMode}
      />
    )}

    <div id="system-info-container">
    <div id="thread-id-container" className="system-info">
      <strong>Thread ID:</strong> <div id="thread-id-text">{localStorage.getItem('currentThreadId')}</div>
    </div>
    <br/>
    <div id="run-id-container" className="system-info">
      <strong>Run ID:</strong> <span id="run-id-text">{localStorage.getItem('currentRunId')}</span>
    </div>   
    </div>
  </div>
  );

  /* JSX COMPONENTS END */

  //
}
